import { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { 
  Send, 
  Mic, 
  MicOff, 
  Loader2, 
  Settings, 
  LogIn,
  LogOut, 
  MessageSquare,
  Pill,
  Check,
  ClipboardList,
  Users,
  ShieldAlert,
  Shield,
  FileText,
  Briefcase,
  CheckCircle,
  Sparkles,
  Plus,
  Menu,
  X,
  ChevronRight,
  MapPin,
  Lock,
  HelpCircle,
  Home,
  Phone,
  Mail,
  AlertTriangle,
  Calendar,
  Heart,
  DoorOpen,
  Building2,
  User,
  Clock,
  Utensils,
  Edit,
  Download,
  BookOpen,
  Trash2,
  ListChecks,
  Search,
  History,
  Sparkle,
  Brush,
  GraduationCap,
  PlayCircle,
  ChevronLeft,
  Lightbulb,
  Zap,
  Bot,
  Sun,
  Moon,
  Sunset,
  ChevronDown,
  ChevronUp,
  Coffee,
  Bell,
  Target,
  Flame,
  Trophy,
  CheckCircle2,
  MessageCircle,
  UserPlus,
  Copy,
  Smartphone,
  Key,
  Volume2,
  VolumeX,
  Siren,
  Ban,
  Footprints,
  XCircle,
  RefreshCw,
  FileStack,
  BarChart3,
  Dumbbell,
  TrendingUp,
  Star,
  Globe,
  Folder,
  Stethoscope,
  Handshake,
  Vote,
  ThumbsUp,
  Scale,
  Wallet,
  ShoppingCart,
  Laptop,
  Award
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Import refactored dashboard components - Core components (niet lazy)
import {
  ZorgVleugelLogo,
  QuickReplyButton,
  ClientCard,
  ClientDetailModal,
  StaffProfileModal,
  TourModal,
  ScheduleView,
  InviteModal,
  RoosterEditModal,
  RoosterModal,
  MedicationModal,
  AIChatModal,
  StagiaireDashboard,
  WerkbegeleiderDashboard,
  BegeleiderOverzicht,
  HomeScreen,
  GroupSelectionPage,
  PageLoader,
  QUICK_REPLIES,
  SHIFT_TASKS,
  THEME,
  getCurrentSection,
  getCurrentShiftType,
  getCurrentTask,
  QUICK_ACTIONS as IMPORTED_QUICK_ACTIONS,
} from '../components/dashboard';

// Lazy-loaded pagina's voor betere performance
const LazyPBGesprekkenPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.PBGesprekkenPage })));
const LazyWeekmenuPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.WeekmenuPage })));
const LazyBoodschappenPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.BoodschappenPage })));
const LazyCorveePage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.CorveePage })));
const LazyHuisafsprakenPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.HuisafsprakenPage })));
const LazyTeamuitjesPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.TeamuitjesPage })));
const LazyVergaderingenPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.VergaderingenPage })));
const LazyFichesPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.FichesPage })));
const LazyTakenPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.TakenPage })));
const LazyClientenPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.ClientenPage })));
const LazyTeamoverlegPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.TeamoverlegPage })));
const LazyMDOPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.MDOPage })));
const LazyMedicatiePage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.MedicatiePage })));
const LazyChecklistPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.ChecklistPage })));
const LazyOverdrachtPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.OverdrachtPage })));
const LazyWifiPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.WifiPage })));
const LazyAfvalkalenderPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.AfvalkalenderPage })));
const LazyZorgplanEvaluatiesPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.ZorgplanEvaluatiesPage })));
const LazyRIEEvaluatiesPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.RIEEvaluatiesPage })));
const LazyRevalidatiePage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.RevalidatiePage })));
const LazyEvaluatieOverzichtPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.EvaluatieOverzichtPage })));
const LazyRoosterPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.RoosterPage })));
const LazyAfsprakenPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.AfsprakenPage })));
const LazyKlusjesPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.KlusjesPage })));
const LazyDagbestedingPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.DagbestedingPage })));
const LazyEetmomentenPage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.EetmomentenPage })));
const LazyCategoryPage = lazy(() => import('../components/dashboard/CategoryPage').then(m => ({ default: m.CategoryPage })));
const LazyVoiceRapportagePage = lazy(() => import('../components/dashboard/FullScreenPages').then(m => ({ default: m.VoiceRapportagePage })));
const LazyRapporteerHelperPage = lazy(() => import('./dashboard/RapporteerHelperPage'));
const LazyRapportageControlePage = lazy(() => import('./dashboard/RapportageControlePage'));
const LazyParkeerHulpPage = lazy(() => import('./dashboard/ParkeerHulpPage'));
const LazyHKZPage = lazy(() => import('./dashboard/HKZPage'));
const LazyInwerkbotPage = lazy(() => import('./dashboard/InwerkbotPage'));
const LazyPBOverzichtPage = lazy(() => import('./dashboard/PBOverzichtPage'));

const API_URL = process.env.REACT_APP_BACKEND_URL;

// UI-specific CATEGORIES with React icons (extends constants)
const UI_CATEGORIES = [
  { 
    id: 'inwerken', 
    name: 'Eerste Dienst', 
    icon: BookOpen, 
    color: '#10B981', 
    highlight: true,
    description: 'Alles voor je eerste dienst',
    subcategories: [
      { id: 'ochtenddienst', name: 'Ochtenddienst', icon: 'Sun' },
      { id: 'avonddienst', name: 'Avonddienst', icon: 'Sunset' },
      { id: 'nachtdienst', name: 'Nachtdienst', icon: 'Moon' },
      { id: 'systemen', name: 'Systemen', icon: 'Laptop' }
    ]
  },
  { 
    id: 'medicatie', 
    name: 'Medicatie', 
    icon: Pill, 
    color: '#7C3AED',
    description: 'Medicatiebeheer',
    subcategories: [
      { id: '5rs', name: '5 J\'s Protocol', icon: 'CheckCircle' },
      { id: 'medicatie_clienten', name: 'Per cliënt', icon: 'User' },
      { id: 'dubbele_controle', name: 'Dubbele controle', icon: 'Lock' },
      { id: 'vermissing', name: 'Bij vermissing', icon: 'AlertTriangle' },
      { id: 'zelfbeheer', name: 'Zelfbeheer', icon: 'Home' }
    ]
  },
  { 
    id: 'clienten', 
    name: 'Cliënten', 
    icon: Users, 
    color: '#EC4899',
    description: 'Cliëntinformatie',
    subcategories: [
      { id: 'profielen', name: 'Profielen', icon: 'User' },
      { id: 'signaleringsplan', name: 'Signaleringsplan', icon: 'BarChart3' },
      { id: 'zorgdoelen', name: 'Zorgdoelen', icon: 'Target' },
      { id: 'contactpersonen', name: 'Contacten', icon: 'Phone' }
    ]
  },
  { 
    id: 'huisafspraken', 
    name: 'Huisafspraken', 
    icon: Home, 
    color: '#F59E0B',
    description: 'Dagritme & regels',
    subcategories: [
      { id: 'dagritme', name: 'Dagritme', icon: 'Clock' },
      { id: 'corvee', name: 'Corvee', icon: 'ClipboardList' },
      { id: 'afval', name: 'Afval', icon: 'Trash2' },
      { id: 'boodschappen', name: 'Boodschappen', icon: 'ShoppingCart' },
      { id: 'financieel', name: 'Financieel', icon: 'Wallet' }
    ]
  },
  { 
    id: 'veiligheid', 
    name: 'Veiligheid & Nood', 
    icon: ShieldAlert, 
    color: '#EF4444',
    description: 'Nood, agressie & protocollen',
    subcategories: [
      { id: 'noodgevallen', name: 'Noodgevallen (112)', icon: 'Siren' },
      { id: 'agressie', name: 'Agressieprotocol', icon: 'AlertTriangle' },
      { id: 'brand', name: 'Brandprotocol', icon: 'Flame' },
      { id: 'meldcode', name: 'Meldcode Geweld', icon: 'Shield' },
      { id: 'alcohol_drugs', name: 'Alcohol & Drugs', icon: 'Ban' },
      { id: 'achterwacht', name: 'Achterwacht bellen', icon: 'Phone' }
    ]
  },
  { 
    id: 'protocollen', 
    name: 'Protocollen', 
    icon: ClipboardList, 
    color: '#8B5CF6',
    description: 'Alle protocollen',
    subcategories: [
      { id: 'rapportage', name: 'Rapportage (SOEP)', icon: 'FileText' },
      { id: 'overdracht_protocol', name: 'Overdracht', icon: 'RefreshCw' },
      { id: 'mic_melding', name: 'MIC Melding', icon: 'ClipboardList' },
      { id: 'agressie', name: 'Agressieprotocol', icon: 'AlertTriangle' },
      { id: 'meldcode', name: 'Meldcode Huiselijk Geweld', icon: 'Shield' },
      { id: 'alcohol_drugs', name: 'Alcohol & Drugs', icon: 'Ban' },
      { id: 'vrijheidsbeperkende', name: 'Vrijheidsbeperkende Maatregelen', icon: 'Lock' },
      { id: 'suicidaliteit', name: 'Suïcidaliteit', icon: 'AlertTriangle' },
      { id: 'medicatie_incidenten', name: 'Medicatie Incidenten', icon: 'Pill' },
      { id: 'valpreventie', name: 'Valpreventie', icon: 'Footprints' },
      { id: 'hygieneprotocol', name: 'Hygiëneprotocol', icon: 'Sparkles' },
      { id: 'achterwacht', name: 'Achterwacht Bellen', icon: 'Phone' },
      { id: 'calamiteiten', name: 'Calamiteitenplan', icon: 'Siren' },
      { id: 'seksueel_gedrag', name: 'Seksueel Grensoverschrijdend Gedrag', icon: 'XCircle' },
      { id: 'gedragscode', name: 'Gedragscode', icon: 'BookOpen' }
    ]
  },
  {
    id: 'primair_proces',
    name: 'Primair Proces',
    icon: Users,
    color: '#10B981',
    description: 'Cliëntprocessen',
    subcategories: [
      { id: 'aanmelding_uitstroom', name: 'Aanmelding tot Uitstroom', icon: 'RefreshCw' },
      { id: 'intake_proces', name: 'Intake Proces', icon: 'ClipboardList' },
      { id: 'zorgplan', name: 'Zorgplan', icon: 'FileStack' },
      { id: 'signaleringsplan', name: 'Signaleringsplan', icon: 'BarChart3' },
      { id: 'risico_inventarisatie', name: 'Risico-inventarisatie Cliënt', icon: 'AlertTriangle' },
      { id: 'krachtwerk', name: 'Krachtwerk Methodiek', icon: 'Dumbbell' },
      { id: 'fasewerken', name: 'Werken met Fases', icon: 'TrendingUp' },
      { id: 'groepsdynamiek', name: 'Groepsdynamiek', icon: 'Users' },
      { id: 'beloningssysteem', name: 'Beloningssysteem', icon: 'Star' },
      { id: 'client_participatie', name: 'Cliëntparticipatie', icon: 'MessageCircle' },
      { id: 'zak_kleedgeld', name: 'Zak- en Kleedgeld', icon: 'Wallet' },
      { id: 'netwerk_client', name: 'Netwerk Cliënt', icon: 'Globe' }
    ]
  },
  {
    id: 'personeel',
    name: 'Personeel',
    icon: Briefcase,
    color: '#F59E0B',
    description: 'Personeelsprocessen',
    subcategories: [
      { id: 'werving_selectie', name: 'Werving & Selectie', icon: 'Search' },
      { id: 'inwerkprocedure', name: 'Inwerkprocedure', icon: 'BookOpen' },
      { id: 'functioneringsgesprek', name: 'Functioneringsgesprek', icon: 'MessageSquare' },
      { id: 'beoordelingsgesprek', name: 'Beoordelingsgesprek', icon: 'BarChart3' },
      { id: 'personeelsdossier', name: 'Personeelsdossier', icon: 'Folder' },
      { id: 'bijscholing', name: 'Bijscholing & Ontwikkeling', icon: 'GraduationCap' },
      { id: 'arbo_rie', name: 'Arbo & RIE', icon: 'Shield' },
      { id: 'bhv_ehbo', name: 'BHV & EHBO', icon: 'Stethoscope' },
      { id: 'nazorg_incident', name: 'Nazorg bij Incidenten', icon: 'Handshake' },
      { id: 'medezeggenschap', name: 'Medezeggenschap (PVT/OR)', icon: 'Vote' }
    ]
  },
  {
    id: 'kwaliteit',
    name: 'Kwaliteitssysteem',
    icon: CheckCircle,
    color: '#6366F1',
    description: 'Kwaliteitsmanagement',
    subcategories: [
      { id: 'directiebeoordeling', name: 'Directiebeoordeling', icon: 'ClipboardList' },
      { id: 'interne_audit', name: 'Interne Audit', icon: 'Search' },
      { id: 'externe_audit', name: 'Externe Audit', icon: 'CheckCircle' },
      { id: 'verbeterregister', name: 'Verbeterregister', icon: 'TrendingUp' },
      { id: 'afwijkingen', name: 'Afwijkingen & Corrigerende Maatregelen', icon: 'AlertTriangle' },
      { id: 'klachten', name: 'Klachtenafhandeling', icon: 'FileText' },
      { id: 'tevredenheid', name: 'Tevredenheidsonderzoek', icon: 'ThumbsUp' },
      { id: 'informatiebeveiliging', name: 'Informatiebeveiliging (NEN-7510)', icon: 'Lock' },
      { id: 'avg_privacy', name: 'AVG & Privacy', icon: 'Key' },
      { id: 'wet_regelgeving', name: 'Wet- en Regelgeving', icon: 'Scale' }
    ]
  },
  { id: 'rapportage', name: 'Rapportage', icon: FileText, color: '#6366F1' },
  { id: 'organisatie', name: 'Organisatie', icon: Building2, color: '#6366F1' },
  { 
    id: 'hkz', 
    name: 'HKZ Management', 
    icon: Award, 
    color: '#8B5CF6',
    highlight: true,
    description: 'Kwaliteitsdocumentatie',
    fullscreenPage: true
  },
  { id: 'overige', name: 'Overige', icon: HelpCircle, color: '#6B7280' },
];

// Note: ScheduleView and TourModal are now imported from '../components/dashboard'

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_caregiver-hub-25/artifacts/7i9n9nvr_Zorg%20Vleugel%20Logo%20Icoon%20Rood.png";

// Quick Actions for common questions
const QUICK_ACTIONS = [
  { icon: Clock, label: "Hoe laat is het avondeten?", question: "Hoe laat is het avondeten en is dit verplicht?" },
  { icon: Pill, label: "5 R's medicatie", question: "Wat zijn de 5 R's van medicatie toediening?" },
  { icon: AlertTriangle, label: "Wat bij noodgeval?", question: "Wat moet ik doen bij een noodgeval?" },
  { icon: Trash2, label: "Afval ophaaldag", question: "Wanneer is de volgende afval ophaaldag?" },
  { icon: Users, label: "Wie is de voorzitter?", question: "Wie is de voorzitter van deze groep?" },
  { icon: GraduationCap, label: "Mijn eerste dienst", question: "Geef me een overzicht van mijn eerste dienst" },
];

export default function DashboardPage() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is a stagiaire or werkbegeleider
  const isStagiaire = user?.role === 'stagiaire';
  const isWerkbegeleider = user?.role === 'werkbegeleider';
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showCategories, setShowCategories] = useState(true);
  
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('groep-zwaluw');
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDetail, setShowClientDetail] = useState(false);
  
  // New states for menu, profile and schedule
  const [activeView, setActiveView] = useState('home'); // Default naar home screen
  const [staffProfile, setStaffProfile] = useState(null);
  const [locationSchedule, setLocationSchedule] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false); // Floating AI chat modal
  const [showAllAppointmentsModal, setShowAllAppointmentsModal] = useState(false); // Modal voor alle afspraken
  const [showMedicationModal, setShowMedicationModal] = useState(false); // Modal voor medicatie overzicht
  const [administeredMedications, setAdministeredMedications] = useState([]); // Track welke medicaties zijn afgetekend
  const [medicationLoading, setMedicationLoading] = useState({}); // Loading state per medicatie
  
  // Rooster states
  const [showRoosterModal, setShowRoosterModal] = useState(false);
  const [roosterData, setRoosterData] = useState({ adelaars: [], zwaluw: [] });
  const [weekRooster, setWeekRooster] = useState([]);
  const [roosterLoading, setRoosterLoading] = useState(false);
  const [showRoosterEditModal, setShowRoosterEditModal] = useState(false);
  const [editingRoosterEntry, setEditingRoosterEntry] = useState(null);
  const [selectedRoosterLocation, setSelectedRoosterLocation] = useState('adelaars'); // 'adelaars' of 'zwaluw'
  const [selectedRoosterMonth, setSelectedRoosterMonth] = useState(0); // 0 = januari, 1 = februari, 2 = maart
  
  // Staff Invite Modal (Admin only)
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // 2FA Secret Modal - voor gebruikers om hun secret te bekijken
  const [show2FASecretModal, setShow2FASecretModal] = useState(false);
  const [twofaSecretData, setTwofaSecretData] = useState(null);
  const [twofaSecretLoading, setTwofaSecretLoading] = useState(false);
  const [twofaSecretCopied, setTwofaSecretCopied] = useState(false);
  
  // Shift Dashboard states
  const [currentShiftType, setCurrentShiftType] = useState(getCurrentShiftType());
  const [selectedShiftView, setSelectedShiftView] = useState(getCurrentShiftType());
  const [completedTasks, setCompletedTasks] = useState([]);
  const [mealPlan, setMealPlan] = useState({
    weekmenu: [
      { dag: 'Maandag', maaltijd: 'Pasta met gehakt en groenten' },
      { dag: 'Dinsdag', maaltijd: 'Aardappelen, sperziebonen en kip' },
      { dag: 'Woensdag', maaltijd: 'Rijst met kippenpoten' },
      { dag: 'Donderdag', maaltijd: 'Wraps met gehakt' },
      { dag: 'Vrijdag', maaltijd: 'Ovenschotel' },
      { dag: 'Zaterdag', maaltijd: 'Cliënt kiest zelf' },
      { dag: 'Zondag', maaltijd: 'Brunch + avond snack' }
    ]
  });
  const [expandedSections, setExpandedSections] = useState(() => {
    // Automatisch de huidige sectie uitklappen
    const currentSection = getCurrentSection();
    return { [currentSection]: true };
  });
  
  // Onboarding & Tour states
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showClientSearch, setShowClientSearch] = useState(false);
  
  // Dienst & Proactieve AI states
  const [currentShift, setCurrentShift] = useState(null);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [proactiveTips, setProactiveTips] = useState([]);
  // Removed showMealPlan state - weekmenu is now only in BegeleiderOverzicht
  
  // Medicatie Reminder states
  const [medicationReminder, setMedicationReminder] = useState(null);
  const [showMedicationPanel, setShowMedicationPanel] = useState(false);
  
  // Groepsagenda states
  const [showAgenda, setShowAgenda] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [appointmentRemindersShown, setAppointmentRemindersShown] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    client_initials: '',
    client_name: '',
    date: new Date().toISOString().split('T')[0],
    time: '10:00',
    location: '',
    description: ''
  });
  
  // Admin Dashboard states
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffActivity, setStaffActivity] = useState([]);
  
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Brandoefening Simulator state
  const [fireDrillActive, setFireDrillActive] = useState(false);
  const [fireDrillStep, setFireDrillStep] = useState(0);
  const [fireDrillTimer, setFireDrillTimer] = useState(0);
  const [fireDrillLocation, setFireDrillLocation] = useState(null);
  const [fireDrillComplete, setFireDrillComplete] = useState(false);
  const [fireDrillScore, setFireDrillScore] = useState(0);
  
  // HomeScreen notifications state
  const [homeNotifications, setHomeNotifications] = useState({});
  
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fireDrillTimerRef = useRef(null);
  const medicationTimerRef = useRef(null);

  // Medicatie schema voor de groep - CORRECT overzicht
  // LOCATIE: Enschotsestraat 98 - Yassin Houba & Mahad Abib
  // LOCATIE: Enschotsestraat 100 - Sietse, Richard & Axcel
  // Yassin Houba: avond (18:00) na het eten
  // Mahad Abib: eigen beheer, avond
  // Axcel: ochtend én avond
  // Sietse: alleen zo nodig (PRN)
  const MEDICATION_SCHEDULE = [
    {
      time: '08:00',
      label: 'Ochtend',
      reminderTime: '07:45',
      clients: [
        { name: 'Axcel', initials: 'A.T.', room: 'Kamer 4', location: 'Enschotsestraat 100', medication: 'Ochtendmedicatie', dosage: '1 tablet', type: 'begeleider', note: 'Begeleider geeft medicatie', instructions: 'Na het ontbijt innemen' },
      ]
    },
    {
      time: '18:00',
      label: 'Avond',
      reminderTime: '17:45',
      clients: [
        { name: 'Yassin', initials: 'Y.H.', room: 'Kamer 1', location: 'Enschotsestraat 98', medication: 'Aripiprazol', dosage: 'Volgens voorschrift', type: 'begeleider', note: 'Begeleider geeft medicatie', instructions: 'NA het avondeten innemen' },
        { name: 'Mahad', initials: 'M.A.', room: 'Kamer 2', location: 'Enschotsestraat 98', medication: 'Avondmedicatie', dosage: 'Volgens voorschrift', type: 'eigen_beheer', note: 'Eigen beheer - herinneren indien nodig', instructions: 'Zelf innemen, begeleider checkt of het is ingenomen' },
        { name: 'Axcel', initials: 'A.T.', room: 'Kamer 4', location: 'Enschotsestraat 100', medication: 'Avondmedicatie', dosage: '1 tablet', type: 'begeleider', note: 'Begeleider geeft medicatie', instructions: 'Na het avondeten innemen' },
      ]
    },
    {
      time: 'ZO NODIG',
      label: 'Zo Nodig (PRN)',
      reminderTime: null,
      isOnDemand: true,
      clients: [
        { name: 'Sietse', initials: 'S.B.', room: 'Kamer 1', location: 'Enschotsestraat 100', medication: 'Zo nodig medicatie', dosage: 'Volgens voorschrift', type: 'begeleider', note: 'Alleen op verzoek van Sietse', instructions: 'Alleen geven wanneer Sietse erom vraagt. Altijd aftekenen!' },
      ]
    }
  ];

  // Brandoefening stappen per locatie
  const FIRE_DRILL_STEPS = {
    'ardennenlaan-68': [
      { action: 'alarm', text: 'Druk op de brandmelder (rode knop)', correctAnswer: 'Alarm geactiveerd' },
      { action: 'bel112', text: 'Bel 112 - Meld: "Brand Ardennenlaan 68"', correctAnswer: '112 gebeld' },
      { action: 'waarschuw', text: 'Waarschuw begeleiders en waakdienst', correctAnswer: 'Collega\'s gewaarschuwd' },
      { action: 'evacueer', text: 'Evacueer bewoners via groene vluchtpijlen', correctAnswer: 'Bewoners geëvacueerd' },
      { action: 'verzamel', text: 'Ga naar verzamelplaats: BUITEN, links van hoofdingang', correctAnswer: 'Op verzamelplaats' },
      { action: 'tel', text: 'Tel of alle bewoners aanwezig zijn', correctAnswer: 'Alle bewoners geteld' },
    ],
    'enschotsestraat-68': [
      { action: 'alarm', text: 'Druk op de brandmelder (rode knop)', correctAnswer: 'Alarm geactiveerd' },
      { action: 'bel112', text: 'Bel 112 - Meld: "Brand Enschotsestraat 68"', correctAnswer: '112 gebeld' },
      { action: 'waarschuw', text: 'Waarschuw begeleiders en waakdienst', correctAnswer: 'Collega\'s gewaarschuwd' },
      { action: 'evacueer', text: 'Evacueer bewoners via Poort naar Tuin', correctAnswer: 'Bewoners geëvacueerd' },
      { action: 'verzamel', text: 'Ga naar verzamelplaats: BUITEN in de TUIN (bij Poort)', correctAnswer: 'Op verzamelplaats' },
      { action: 'tel', text: 'Tel of alle bewoners aanwezig zijn', correctAnswer: 'Alle bewoners geteld' },
    ],
    'enschotsestraat-98': [
      { action: 'alarm', text: 'Druk op de brandmelder (rode knop)', correctAnswer: 'Alarm geactiveerd' },
      { action: 'bel112', text: 'Bel 112 - Meld: "Brand Enschotsestraat 98"', correctAnswer: '112 gebeld' },
      { action: 'waarschuw', text: 'Waarschuw begeleiders en waakdienst', correctAnswer: 'Collega\'s gewaarschuwd' },
      { action: 'evacueer', text: 'Evacueer bewoners via Gang naar Tuin', correctAnswer: 'Bewoners geëvacueerd' },
      { action: 'verzamel', text: 'Ga naar verzamelplaats: BUITEN bij de TUIN (achter gebouw)', correctAnswer: 'Op verzamelplaats' },
      { action: 'tel', text: 'Tel of alle bewoners aanwezig zijn', correctAnswer: 'Alle bewoners geteld' },
    ],
    'enschotsestraat-100': [
      { action: 'alarm', text: 'Druk op de brandmelder (rode knop)', correctAnswer: 'Alarm geactiveerd' },
      { action: 'bel112', text: 'Bel 112 - Meld: "Brand Enschotsestraat 100"', correctAnswer: '112 gebeld' },
      { action: 'waarschuw', text: 'Waarschuw begeleiders en waakdienst', correctAnswer: 'Collega\'s gewaarschuwd' },
      { action: 'evacueer', text: 'Evacueer bewoners via Trap naar BG en dan Verzamelplaats', correctAnswer: 'Bewoners geëvacueerd' },
      { action: 'verzamel', text: 'Ga naar verzamelplaats: BUITEN, naast hoofdingang', correctAnswer: 'Op verzamelplaats' },
      { action: 'tel', text: 'Tel of alle bewoners aanwezig zijn', correctAnswer: 'Alle bewoners geteld' },
    ],
  };

  // Start brandoefening
  const startFireDrill = (location) => {
    const locationKey = location.toLowerCase().replace(/\s+/g, '-');
    if (!FIRE_DRILL_STEPS[locationKey]) {
      toast.error('Locatie niet gevonden voor brandoefening');
      return;
    }
    
    setFireDrillLocation(locationKey);
    setFireDrillActive(true);
    setFireDrillStep(0);
    setFireDrillTimer(0);
    setFireDrillComplete(false);
    setFireDrillScore(0);
    
    // Start timer
    fireDrillTimerRef.current = setInterval(() => {
      setFireDrillTimer(prev => prev + 1);
    }, 1000);
    
    toast.success('Brandoefening gestart! Volg de stappen zo snel mogelijk.');
  };

  // Voltooi stap in brandoefening
  const completeFireDrillStep = () => {
    const steps = FIRE_DRILL_STEPS[fireDrillLocation];
    if (fireDrillStep < steps.length - 1) {
      setFireDrillStep(prev => prev + 1);
      setFireDrillScore(prev => prev + 10);
    } else {
      // Oefening voltooid
      clearInterval(fireDrillTimerRef.current);
      setFireDrillComplete(true);
      
      // Bereken score gebaseerd op tijd (bonus voor snelheid)
      const timeBonus = Math.max(0, 60 - fireDrillTimer) * 2;
      const finalScore = fireDrillScore + 10 + timeBonus;
      setFireDrillScore(finalScore);
      
      toast.success(`Brandoefening voltooid! Score: ${finalScore} punten (${fireDrillTimer} seconden)`);
    }
  };

  // Stop brandoefening
  const stopFireDrill = () => {
    clearInterval(fireDrillTimerRef.current);
    setFireDrillActive(false);
    setFireDrillLocation(null);
    setFireDrillStep(0);
    setFireDrillTimer(0);
    setFireDrillComplete(false);
  };

  // Update shift type elke minuut
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentShiftType(getCurrentShiftType());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Preload CategoryPage en andere veelgebruikte componenten voor snellere navigatie
  useEffect(() => {
    // Preload CategoryPage direct na mount
    import('../components/dashboard/CategoryPage');
    // Preload VoiceRapportagePage
    import('../components/dashboard/FullScreenPages').then(m => m.VoiceRapportagePage);
  }, []);

  // Toggle sectie uitklappen/inklappen
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Bereken voortgang per sectie
  const getSectionProgress = (shiftType, sectionId) => {
    const shift = SHIFT_TASKS[shiftType];
    const section = shift?.sections?.find(s => s.id === sectionId);
    if (!section) return 0;
    
    const completedCount = section.tasks.filter((_, i) => 
      completedTasks.includes(`${shiftType}-${sectionId}-${i}`)
    ).length;
    return Math.round((completedCount / section.tasks.length) * 100);
  };

  // Bereken totale voortgang per dienst (alle secties)
  const getShiftProgress = (shiftType) => {
    const shift = SHIFT_TASKS[shiftType];
    if (!shift?.sections) return 0;
    
    let totalTasks = 0;
    let completed = 0;
    
    shift.sections.forEach(section => {
      section.tasks.forEach((_, i) => {
        totalTasks++;
        if (completedTasks.includes(`${shiftType}-${section.id}-${i}`)) {
          completed++;
        }
      });
    });
    
    return totalTasks > 0 ? Math.round((completed / totalTasks) * 100) : 0;
  };

  // Toggle taak als voltooid (met sectie)
  const toggleTask = (shiftType, sectionId, taskIndex) => {
    const key = `${shiftType}-${sectionId}-${taskIndex}`;
    setCompletedTasks(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Check if user is new (first visit)
  useEffect(() => {
    const hasSeenTour = localStorage.getItem(`ilias_tour_${user?.id}`);
    if (user && !hasSeenTour) {
      setShowTour(true);
    }
  }, [user]);

  // Filter sessions based on search
  const filteredSessions = sessions.filter(s => 
    !searchQuery || s.last_message?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      fetchLocations();
      fetchSessions();
      fetchStaffProfile();
      fetchCurrentShift();
      fetchProactiveTips();
      
      // Determine which location to use
      // Zorgmanager/admin heeft geen vaste locatie, gebruik default groep-zwaluw
      const isManagerOrAdmin = user.role === 'zorgmanager' || user.role === 'admin';
      const locationToUse = user.selected_location || selectedLocation || (isManagerOrAdmin ? 'groep-zwaluw' : null);
      
      if (!locationToUse) {
        setShowLocationPicker(true);
      } else {
        if (user.selected_location && user.selected_location !== selectedLocation) {
          setSelectedLocation(user.selected_location);
        } else if (isManagerOrAdmin && !selectedLocation) {
          // Zorgmanager start met groep-zwaluw als default
          setSelectedLocation('groep-zwaluw');
        }
        // Always fetch clients for the active location
        fetchClients(locationToUse);
      }
    }
  }, [user, navigate]);

  // Refresh proactive tips every 30 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchProactiveTips();
    }, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedLocation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Laad medicatie-administraties van vandaag
  useEffect(() => {
    const fetchMedicationAdministrations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/medication/today`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.administrations) {
          setAdministeredMedications(response.data.administrations);
        }
      } catch (error) {
        console.error('Error fetching medication administrations:', error);
      }
    };
    
    if (user) {
      fetchMedicationAdministrations();
    }
  }, [user]);

  // Check of een medicatie is afgetekend
  const isMedicationGiven = (scheduleTime, clientName, medication) => {
    return administeredMedications.some(
      admin => admin.schedule_time === scheduleTime && 
               admin.client_name === clientName && 
               admin.medication === medication
    );
  };

  // Get medication administration details
  const getMedicationAdmin = (scheduleTime, clientName, medication) => {
    return administeredMedications.find(
      admin => admin.schedule_time === scheduleTime && 
               admin.client_name === clientName && 
               admin.medication === medication
    );
  };

  // Laad rooster bij opstarten
  useEffect(() => {
    if (user) {
      fetchTodaysRooster();
    }
  }, [user]);

  // Laad HomeScreen notifications
  useEffect(() => {
    const fetchHomeNotifications = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/begeleider/overzicht`);
        const data = response.data;
        
        // Calculate notifications based on data
        const notifications = {};
        
        // Boodschappen count (unchecked items)
        if (data?.boodschappen?.lijst) {
          const uncheckedCount = Object.values(data.boodschappen.lijst || {}).flat().filter(item => {
            const status = data.boodschappen.item_status?.[item];
            return !status?.afgevinkt;
          }).length;
          if (uncheckedCount > 0) notifications.boodschappen = uncheckedCount;
        }
        
        // Vergadering vandaag
        const today = new Date().toISOString().split('T')[0];
        if (data?.vergaderingen) {
          const todayMeeting = data.vergaderingen.find(v => v.datum === today);
          if (todayMeeting) {
            notifications.vergadering = true;
            notifications.vergaderingDetail = `${todayMeeting.tijd} - ${todayMeeting.type}`;
          }
        }
        
        // Afval ophaaldag check
        if (data?.afvalkalender?.ophaaldata) {
          const todayFormatted = new Date().toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const isAfvaldag = data.afvalkalender.ophaaldata.includes(todayFormatted);
          if (isAfvaldag) {
            notifications.afval = true;
            notifications.afvalDetail = 'Vandaag restafval ophalen!';
          }
        }
        
        // Teamoverleg vandaag (teamoverleg is now an object, not array)
        if (data?.teamoverleg && data.teamoverleg.datum === today) {
          notifications.teamoverleg = 1;
        }
        
        // MDO vandaag (mdo might be object or array)
        if (data?.mdo) {
          if (Array.isArray(data.mdo)) {
            const todayMdo = data.mdo.find(m => m.datum === today);
            if (todayMdo) {
              notifications.mdo = 1;
            }
          } else if (data.mdo.datum === today) {
            notifications.mdo = 1;
          }
        }
        
        setHomeNotifications(notifications);
      } catch (error) {
        console.error('Error fetching home notifications:', error);
      }
    };
    
    if (user && activeView === 'home') {
      fetchHomeNotifications();
      // Refresh every 5 minutes
      const interval = setInterval(fetchHomeNotifications, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user, activeView]);

  // Markeer medicatie als afgetekend op apotheeklijst
  const administerMedication = async (scheduleTime, client, medication) => {
    const key = `${scheduleTime}-${client.name}-${medication}`;
    setMedicationLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/medication/administer`, {
        schedule_time: scheduleTime,
        client_name: client.name,
        medication: medication
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      const newAdmin = {
        schedule_time: scheduleTime,
        client_name: client.name,
        medication: medication,
        given_by_name: user?.name || 'Begeleider',
        given_at: new Date().toISOString()
      };
      setAdministeredMedications(prev => [...prev, newAdmin]);
      toast.success(`${client.name} - ${medication} afgetekend op apotheeklijst`);
    } catch (error) {
      console.error('Error administering medication:', error);
      toast.error('Kon medicatie niet registreren');
    } finally {
      setMedicationLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Verwijder medicatie-registratie (undo)
  const undoMedicationAdministration = async (scheduleTime, clientName, medication) => {
    const key = `${scheduleTime}-${clientName}-${medication}`;
    setMedicationLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/medication/administer`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          schedule_time: scheduleTime,
          client_name: clientName,
          medication: medication
        }
      });
      
      // Update local state
      setAdministeredMedications(prev => 
        prev.filter(admin => 
          !(admin.schedule_time === scheduleTime && 
            admin.client_name === clientName && 
            admin.medication === medication)
        )
      );
      toast.info(`↩️ Aftekening ongedaan gemaakt voor ${clientName}`);
    } catch (error) {
      console.error('Error undoing medication administration:', error);
      toast.error('Kon registratie niet ongedaan maken');
    } finally {
      setMedicationLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Rooster functies
  const fetchTodaysRooster = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/schedule/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoosterData(response.data);
    } catch (error) {
      console.error('Error fetching rooster:', error);
    }
  };

  const fetchWeekRooster = async () => {
    setRoosterLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Haal alle data voor januari, februari en maart op
      const response = await axios.get(`${API_URL}/api/schedule/months`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeekRooster(response.data.schedules || []);
    } catch (error) {
      console.error('Error fetching rooster:', error);
    } finally {
      setRoosterLoading(false);
    }
  };

  const saveRoosterEntry = async (entry) => {
    try {
      const token = localStorage.getItem('token');
      // Stuur alleen de vereiste velden naar de API
      const scheduleData = {
        date: entry.date,
        location: entry.location,
        staff_name: entry.staff_name,
        shift_type: entry.shift_type,
        start_time: entry.start_time,
        end_time: entry.end_time,
        notes: entry.notes || null
      };
      await axios.post(`${API_URL}/api/schedule`, scheduleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Rooster bijgewerkt voor ${entry.date}`);
      fetchWeekRooster();
      fetchTodaysRooster();
      setShowRoosterEditModal(false);
      setEditingRoosterEntry(null);
    } catch (error) {
      console.error('Error saving rooster:', error);
      toast.error(error.response?.data?.detail || 'Kon rooster niet opslaan');
    }
  };

  const deleteRoosterEntry = async (date, location, shiftType = 'dagdienst') => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/schedule/${date}/${location}/${shiftType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Roosterinvoer verwijderd');
      fetchWeekRooster();
      fetchTodaysRooster();
    } catch (error) {
      console.error('Error deleting rooster:', error);
      toast.error(error.response?.data?.detail || 'Kon roosterinvoer niet verwijderen');
    }
  };

  // Invite staff member (Admin only)
  // Download rooster als PDF
  const downloadRoosterPDF = () => {
    const location = selectedRoosterLocation;
    const month = selectedRoosterMonth; // 0 = jan, 1 = feb, 2 = maart
    const year = 2026;
    const monthNames = ['Januari', 'Februari', 'Maart'];
    const monthName = monthNames[month];
    
    const isAdelaars = location === 'adelaars';
    const locationName = isAdelaars ? 'Adelaars' : 'Zwaluw';
    const locationAddress = isAdelaars ? 'Ardennenlaan 68' : 'Enschotsestraat 98 & 100';
    const locationColor = isAdelaars ? '#3b82f6' : '#22c55e';
    
    // Dienst tijden per locatie
    const dagTijden = isAdelaars ? '08:00 - 20:00' : '08:00 - 19:00';
    const nachtTijden = isAdelaars ? '20:00 - 07:00' : '19:00 - 07:00';
    
    // Bepaal start- en einddatum per maand
    // Januari start vanaf 17, andere maanden vanaf 1
    const startDay = month === 0 ? 17 : 1;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let day = startDay; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const date = new Date(year, month, day);
      const dayName = date.toLocaleDateString('nl-NL', { weekday: 'short' });
      const fullDate = date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
      
      const dagEntry = weekRooster.find(s => s.date === dateStr && s.location === location && s.shift_type === 'dagdienst');
      const nachtEntry = weekRooster.find(s => s.date === dateStr && s.location === location && s.shift_type === 'achterwacht');
      
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();
      
      days.push({ 
        day, 
        dateStr, 
        dayName, 
        fullDate,
        dagEntry, 
        nachtEntry,
        isWeekend,
        isToday
      });
    }
    
    // Periode tekst
    const periodText = month === 0 
      ? `17 - 31 januari ${year}` 
      : `1 - ${daysInMonth} ${monthName.toLowerCase()} ${year}`;
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Rooster ${locationName} - ${monthName} ${year}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            padding: 30px; 
            color: #333;
            background: white;
          }
          
          /* Header */
          .header { 
            margin-bottom: 25px;
            padding-bottom: 15px;
            border-bottom: 2px solid #1e3a5f;
          }
          .header-top {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .title { 
            font-size: 22px; 
            font-weight: 700; 
            color: #1e3a5f;
            letter-spacing: -0.5px;
          }
          .location-tag {
            background: ${locationColor};
            color: white;
            padding: 6px 14px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
          }
          .subtitle { 
            color: #666; 
            margin-top: 4px; 
            font-size: 13px;
          }
          
          /* Info box */
          .info-box {
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            padding: 12px 16px;
            margin-bottom: 20px;
            display: flex;
            gap: 40px;
            font-size: 12px;
          }
          .info-item {
            display: flex;
            gap: 8px;
          }
          .info-label {
            color: #666;
          }
          .info-value {
            font-weight: 600;
            color: #333;
          }
          
          /* Table */
          table { 
            width: 100%; 
            border-collapse: collapse;
            font-size: 12px;
          }
          th { 
            background: #1e3a5f;
            color: white; 
            padding: 12px 10px;
            text-align: left;
            font-weight: 600;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          th:first-child { border-radius: 4px 0 0 0; }
          th:last-child { border-radius: 0 4px 0 0; }
          
          td { 
            border-bottom: 1px solid #e9ecef;
            padding: 10px;
            vertical-align: middle;
          }
          tr:last-child td {
            border-bottom: 2px solid #1e3a5f;
          }
          tr:last-child td:first-child { border-radius: 0 0 0 4px; }
          tr:last-child td:last-child { border-radius: 0 0 4px 0; }
          
          .col-dag { 
            width: 45px; 
            font-weight: 600;
            color: #666;
            text-transform: uppercase;
            font-size: 11px;
          }
          .col-datum { 
            width: 70px;
            color: #333;
          }
          .col-dienst {
            font-weight: 500;
            color: #1e3a5f;
          }
          
          .empty { color: #ccc; }
          
          /* Footer */
          .footer { 
            margin-top: 25px;
            padding-top: 15px;
            border-top: 1px solid #e9ecef;
            font-size: 10px; 
            color: #999;
            text-align: center;
          }
          
          @media print { 
            body { padding: 15px; }
            @page { margin: 1.5cm; }
            th { background: #1e3a5f !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-top">
            <div class="title">Zorg-Vleugel</div>
            <div class="location-tag">${locationName} — ${locationAddress}</div>
          </div>
          <div class="subtitle">Begeleidersrooster ${monthName} ${year}</div>
        </div>
        
        <div class="info-box">
          <div class="info-item">
            <span class="info-label">Periode:</span>
            <span class="info-value">${periodText}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Dagdienst:</span>
            <span class="info-value">${dagTijden}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Achterwacht:</span>
            <span class="info-value">${nachtTijden}</span>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Dag</th>
              <th>Datum</th>
              <th>Dagdienst</th>
              <th>Achterwacht</th>
            </tr>
          </thead>
          <tbody>
            ${days.map(d => `
              <tr class="${d.isToday ? 'today' : ''} ${d.isWeekend ? 'weekend' : ''}">
                <td class="col-dag">${d.dayName}</td>
                <td class="col-datum">${d.fullDate}</td>
                <td class="col-dienst">${d.dagEntry ? d.dagEntry.staff_name : '<span class="empty">—</span>'}</td>
                <td class="col-dienst">${d.nachtEntry ? d.nachtEntry.staff_name : '<span class="empty">—</span>'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          Gegenereerd op ${new Date().toLocaleString('nl-NL')} • Zorg-Vleugel Dashboard
        </div>
      </body>
      </html>
    `;
    
    // Open print dialog
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
    
    toast.success(`PDF ${locationName} - ${monthName} wordt gegenereerd...`);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Open AI chat met schone staat en optionele vraag
  const openAiChat = (question = '', autoSend = false) => {
    setMessages([]); // Clear vorige berichten
    setInput(question);
    setShowAiChat(true);
    
    // Als autoSend true is, stuur de vraag direct
    if (autoSend && question) {
      setTimeout(() => {
        sendMessage(question);
      }, 100);
    }
  };

  // Open brandoefening direct (zonder chat)
  const openBrandoefening = () => {
    setMessages([{
      id: Date.now().toString(),
      role: 'assistant',
      content: `**BRANDOEFENING SIMULATOR**

Kies de locatie waar je de brandoefening wilt doen:`,
      created_at: new Date().toISOString(),
      quickReplies: [
        { text: 'Ardennenlaan 68', action: 'fire-drill' },
        { text: 'Enschotsestraat 68', action: 'fire-drill' },
        { text: 'Enschotsestraat 98', action: 'fire-drill' },
        { text: 'Enschotsestraat 100', action: 'fire-drill' },
      ]
    }]);
    setInput('');
    setShowAiChat(true);
  };

  const fetchCurrentShift = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/shift/current`);
      setCurrentShift(response.data);
    } catch (error) {
      console.error('Failed to fetch current shift:', error);
    }
  };

  const fetchProactiveTips = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedLocation) params.append('location_slug', selectedLocation);
      if (currentShift?.shift_type) params.append('shift_type', currentShift.shift_type);
      
      const response = await axios.get(`${API_URL}/api/ai/proactive-tips?${params}`);
      setProactiveTips(response.data.tips || []);
    } catch (error) {
      console.error('Failed to fetch proactive tips:', error);
    }
  };

  const startShift = async (shiftType) => {
    try {
      await axios.post(`${API_URL}/api/shift/start`, {
        shift_type: shiftType,
        location_slug: selectedLocation
      });
      setCurrentShift({ shift_type: shiftType, location_slug: selectedLocation });
      setShowShiftModal(false);
      toast.success(`${shiftType.charAt(0).toUpperCase() + shiftType.slice(1)}dienst gestart!`);
      fetchProactiveTips();
    } catch (error) {
      toast.error('Kon dienst niet starten');
    }
  };

  // ============================================
  // GROEPSAGENDA FUNCTIES
  // ============================================
  
  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/appointments/upcoming?days=14`);
      setAppointments(response.data.appointments || []);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    }
  };

  const fetchTodaysAppointments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/appointments/today`);
      const data = response.data;
      setTodaysAppointments(data.appointments || []);
      
      // Show reminder toast if there are appointments today and not shown yet
      if (data.has_appointments && !appointmentRemindersShown) {
        const count = data.count;
        const appointments = data.appointments || [];
        
        // Create a detailed message with all appointments
        const appointmentsList = appointments.slice(0, 5).map(apt => 
          `• ${apt.time} - ${apt.description} (${apt.client_name})`
        ).join('\n');
        
        toast(
          <div 
            className="cursor-pointer" 
            onClick={() => {
              // Show all appointments in a more detailed toast
              toast.dismiss();
              setShowNewAppointmentModal(false);
              // Scroll to agenda section or show modal
              const agendaSection = document.querySelector('[data-testid="agenda-section"]');
              if (agendaSection) agendaSection.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <div className="font-semibold text-base mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {count} afspraak{count > 1 ? 'en' : ''} vandaag
            </div>
            <div className="text-sm text-slate-600 whitespace-pre-line">{appointmentsList}</div>
            {appointments.length > 5 && (
              <div className="text-xs text-blue-600 mt-1">+{appointments.length - 5} meer...</div>
            )}
            <div className="text-xs text-slate-400 mt-2">Klik voor details</div>
          </div>,
          {
            duration: 10000,
          }
        );
        setAppointmentRemindersShown(true);
      }
    } catch (error) {
      console.error('Failed to fetch today\'s appointments:', error);
    }
  };

  const createAppointment = async () => {
    if (!newAppointment.client_name || !newAppointment.date || !newAppointment.time || !newAppointment.description) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/appointments`, {
        ...newAppointment,
        location_slug: selectedLocation
      });
      toast.success('Afspraak toegevoegd!');
      setShowNewAppointmentModal(false);
      setNewAppointment({
        client_initials: '',
        client_name: '',
        date: new Date().toISOString().split('T')[0],
        time: '10:00',
        location: '',
        description: ''
      });
      fetchAppointments();
      fetchTodaysAppointments();
    } catch (error) {
      toast.error('Kon afspraak niet aanmaken');
    }
  };

  const deleteAppointment = async (appointmentId) => {
    try {
      await axios.delete(`${API_URL}/api/appointments/${appointmentId}`);
      toast.success('Afspraak verwijderd');
      fetchAppointments();
      fetchTodaysAppointments();
    } catch (error) {
      toast.error('Kon afspraak niet verwijderen');
    }
  };

  // Fetch appointments on mount and when location changes
  useEffect(() => {
    if (selectedLocation) {
      fetchAppointments();
      fetchTodaysAppointments();
    }
  }, [selectedLocation]);

  // Client Search Function
  const searchClients = (query) => {
    setClientSearchQuery(query);
    if (!query.trim() || !clientData?.addresses) {
      setClientSearchResults([]);
      return;
    }
    
    const searchLower = query.toLowerCase();
    const results = [];
    
    clientData.addresses.forEach(addr => {
      addr.rooms?.forEach(room => {
        if (room.client) {
          const client = room.client;
          const matchesName = client.name?.toLowerCase().includes(searchLower);
          const matchesInitials = client.initials?.toLowerCase().includes(searchLower);
          const matchesDiagnosis = client.diagnosis?.some(d => d.toLowerCase().includes(searchLower));
          
          if (matchesName || matchesInitials || matchesDiagnosis) {
            results.push({
              ...client,
              room_number: room.room_number,
              address: addr.address
            });
          }
        }
      });
    });
    
    setClientSearchResults(results);
  };

  // Medicatie Reminder System
  useEffect(() => {
    // Track welke medicatieronden al zijn getoond (per tijdstip)
    const shownReminders = new Set();
    
    const checkMedicationTime = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      
      MEDICATION_SCHEDULE.forEach(schedule => {
        // Skip "zo nodig" medicaties - deze hebben geen vaste tijd
        if (schedule.isOnDemand || !schedule.reminderTime) return;
        
        const [schedHour, schedMinute] = schedule.reminderTime.split(':').map(Number);
        const [medHour, medMinute] = schedule.time.split(':').map(Number);
        
        // Check if it's reminder time
        const reminderStart = schedHour * 60 + schedMinute;
        const medicationTime = medHour * 60 + medMinute;
        const currentTimeMinutes = currentHour * 60 + currentMinute;
        
        // Unieke key voor deze medicatieronde (per dag en tijd)
        const todayKey = `${now.toDateString()}-${schedule.time}`;
        
        // Show reminder from 15 min before until 15 min after medication time
        if (currentTimeMinutes >= reminderStart && currentTimeMinutes <= medicationTime + 15) {
          // Check of we deze reminder al hebben getoond vandaag
          if (!shownReminders.has(todayKey)) {
            shownReminders.add(todayKey);
            
            setMedicationReminder({
              time: schedule.time,
              clients: schedule.clients,
              isUrgent: currentTimeMinutes >= medicationTime - 5 && currentTimeMinutes <= medicationTime + 5
            });
            
            // Show toast notification - slechts één keer
            const minutesUntil = medicationTime - currentTimeMinutes;
            const clientsList = schedule.clients.map(c => `• ${c.name} - ${c.medication}`).join('\n');
            
            if (minutesUntil > 0 && minutesUntil <= 15) {
              toast(
                <div 
                  className="cursor-pointer"
                  onClick={() => {
                    toast.dismiss();
                    setShowMedicationPanel(true);
                  }}
                >
                  <div className="font-semibold text-base mb-2 flex items-center gap-2">
                    <Pill className="w-4 h-4" />
                    Medicatie over {minutesUntil} minuten!
                  </div>
                  <div className="text-sm font-medium text-orange-600 mb-1">Tijd: {schedule.time}</div>
                  <div className="text-sm text-slate-600 whitespace-pre-line">{clientsList}</div>
                  <div className="text-xs text-slate-400 mt-2">Klik voor 5 R&apos;s checklist</div>
                </div>,
                {
                  duration: 15000,
                  id: `medication-${schedule.time}`, // Unieke ID voorkomt duplicaten
                }
              );
            } else if (minutesUntil <= 0 && minutesUntil >= -5) {
              toast(
                <div 
                  className="cursor-pointer"
                  onClick={() => {
                    toast.dismiss();
                    setShowMedicationPanel(true);
                  }}
                >
                  <div className="font-semibold text-base mb-2 text-red-600">⏰ MEDICATIETIJD!</div>
                  <div className="text-sm font-medium text-red-500 mb-1">Het is nu {schedule.time}</div>
                  <div className="text-sm text-slate-600 whitespace-pre-line">{clientsList}</div>
                  <div className="text-xs text-red-400 mt-2 font-medium">Klik om te beginnen</div>
                </div>,
                {
                  duration: 20000,
                  id: `medication-urgent-${schedule.time}`, // Unieke ID voorkomt duplicaten
                }
              );
            }
          }
        } else if (currentTimeMinutes > medicationTime + 15) {
          // Reset na het tijdvenster - verwijder uit shownReminders zodat het morgen weer kan
          setMedicationReminder(null);
        }
      });
    };
    
    // Check immediately on mount
    checkMedicationTime();
    
    // Check every minute
    medicationTimerRef.current = setInterval(checkMedicationTime, 60000);
    
    return () => {
      if (medicationTimerRef.current) {
        clearInterval(medicationTimerRef.current);
      }
    };
  }, []); // Lege dependency array - draait alleen bij mount

  // Test functie om medicatie-reminder te simuleren (voor demonstratie)
  const triggerMedicationReminder = (isUrgent = false) => {
    setMedicationReminder({
      time: '18:00',
      clients: MEDICATION_SCHEDULE[0].clients,
      isUrgent: isUrgent
    });
    
    if (isUrgent) {
      toast.error(
        `⏰ MEDICATIETIJD! Het is 18:00`,
        {
          duration: 15000,
          action: {
            label: 'Bekijken',
            onClick: () => setShowMedicationPanel(true)
          }
        }
      );
    } else {
      toast.warning(
        `Medicatie over 15 minuten! (18:00)`,
        {
          duration: 10000,
          action: {
            label: 'Bekijken',
            onClick: () => setShowMedicationPanel(true)
          }
        }
      );
    }
  };

  // ============================================
  // ADMIN DASHBOARD FUNCTIES
  // ============================================
  
  const fetchAdminDashboard = async () => {
    if (user?.role !== 'admin') return;
    try {
      const response = await axios.get(`${API_URL}/api/admin/dashboard`);
      setAdminData(response.data);
    } catch (error) {
      console.error('Failed to fetch admin dashboard:', error);
    }
  };

  const fetchStaffList = async () => {
    if (user?.role !== 'admin') return;
    try {
      const response = await axios.get(`${API_URL}/api/admin/staff`);
      setStaffList(response.data.staff || []);
    } catch (error) {
      console.error('Failed to fetch staff list:', error);
    }
  };

  const fetchActivityLogs = async (days = 1) => {
    if (user?.role !== 'admin') return;
    try {
      const response = await axios.get(`${API_URL}/api/admin/activity-logs?days=${days}`);
      setActivityLogs(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    }
  };

  const fetchStaffActivity = async (staffId) => {
    if (user?.role !== 'admin') return;
    try {
      const response = await axios.get(`${API_URL}/api/admin/staff/${staffId}/activity?days=7`);
      setStaffActivity(response.data.activities || []);
      setSelectedStaff(response.data.staff);
    } catch (error) {
      console.error('Failed to fetch staff activity:', error);
    }
  };

  // Log task completion to backend
  const logTaskCompletion = async (taskId, taskName, completed) => {
    try {
      await axios.post(`${API_URL}/api/activity/task-complete`, {
        task_id: taskId,
        task_name: taskName,
        shift_type: selectedShiftView,
        completed: completed
      });
    } catch (error) {
      console.error('Failed to log task completion:', error);
    }
  };

  // Fetch admin data when admin opens panel
  useEffect(() => {
    if (showAdminPanel && user?.role === 'admin') {
      fetchAdminDashboard();
      fetchStaffList();
      fetchActivityLogs(1);
    }
  }, [showAdminPanel, user]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations`);
      setLocations(response.data);
      // If begeleider has only one location, auto-select it
      if (response.data.length === 1 && user?.role !== 'admin') {
        const loc = response.data[0];
        selectLocation(loc);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchClients = async (locationSlug) => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${locationSlug}`);
      setClientData(response.data);
      // Also fetch location schedule
      fetchLocationSchedule(locationSlug);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchStaffProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/staff/profile`);
      setStaffProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch staff profile:', error);
    }
  };

  const fetchLocationSchedule = async (locationSlug) => {
    try {
      const response = await axios.get(`${API_URL}/api/schedule/location/${locationSlug}`);
      setLocationSchedule(response.data);
    } catch (error) {
      console.error('Failed to fetch location schedule:', error);
    }
  };

  const selectLocation = async (loc) => {
    const slug = loc.slug || loc.id;
    try {
      await axios.post(`${API_URL}/api/auth/select-location`, { location_slug: slug });
      setSelectedLocation(slug);
      setShowLocationPicker(false);
      fetchClients(slug);
      toast.success(`${loc.name} geselecteerd`);
    } catch (error) {
      console.error('Location select error:', error);
      toast.error('Kon locatie niet selecteren');
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  const loadSession = async (sid) => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/history?session_id=${sid}`);
      setMessages(response.data.reverse());
      setSessionId(sid);
      setShowCategories(false);
      setShowSidebar(false);
    } catch (error) {
      toast.error('Kon gesprek niet laden');
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setSelectedCategory(null);
    setShowCategories(true);
  };

  // Handle quick reply click - check for special commands
  const handleQuickReply = (text) => {
    // Check voor brandoefening commando
    if (text.includes('Start brandoefening')) {
      // Open locatie selectie modal
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        content: `**BRANDOEFENING SIMULATOR**

Kies de locatie waar je de brandoefening wilt doen:`,
        created_at: new Date().toISOString(),
        quickReplies: [
          { text: 'Ardennenlaan 68', action: 'fire-drill' },
          { text: 'Enschotsestraat 68', action: 'fire-drill' },
          { text: 'Enschotsestraat 98', action: 'fire-drill' },
          { text: 'Enschotsestraat 100', action: 'fire-drill' },
        ]
      }]);
      return;
    }
    
    // Check voor brandoefening locatie selectie
    const fireDrillLocations = ['Ardennenlaan 68', 'Enschotsestraat 68', 'Enschotsestraat 98', 'Enschotsestraat 100'];
    if (fireDrillLocations.some(loc => text.includes(loc)) && !fireDrillActive) {
      const location = fireDrillLocations.find(loc => text.includes(loc));
      if (location) {
        startFireDrill(location);
        return;
      }
    }
    
    // Normale quick reply -> stuur als bericht
    sendMessage(text);
  };

  // Functie om 2FA secret op te halen
  const fetch2FASecret = async () => {
    setTwofaSecretLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/my-2fa-secret`);
      setTwofaSecretData(response.data);
      setShow2FASecretModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon 2FA gegevens niet ophalen');
    } finally {
      setTwofaSecretLoading(false);
    }
  };

  const copy2FASecret = () => {
    if (twofaSecretData?.secret) {
      navigator.clipboard.writeText(twofaSecretData.secret);
      setTwofaSecretCopied(true);
      toast.success('Secret gekopieerd!');
      setTimeout(() => setTwofaSecretCopied(false), 2000);
    }
  };

  // TTS State and Audio Ref
  const audioRef = useRef(null);
  const [ttsEnabled, setTtsEnabled] = useState(false);  // Standaard UIT - begeleider kiest zelf
  const [ttsPlaying, setTtsPlaying] = useState(false);

  // Play TTS for AI response - CHUNKED for fast start + full content
  const playTTS = async (text) => {
    if (!ttsEnabled || !text) return;
    
    try {
      setTtsPlaying(true);
      
      // Strip markdown
      let cleanText = text
        .replace(/[#*_`~\[\]()]/g, '')
        .replace(/\|.*?\|/g, '')
        .replace(/---+/g, '')
        .replace(/\n+/g, ' ')
        .trim();
      
      if (!cleanText) {
        setTtsPlaying(false);
        return;
      }
      
      // Split into chunks of ~500 chars at sentence boundaries
      const chunks = [];
      let remaining = cleanText;
      
      while (remaining.length > 0) {
        if (remaining.length <= 500) {
          chunks.push(remaining);
          break;
        }
        
        // Find sentence boundary within first 500 chars
        let cutPoint = remaining.substring(0, 500).lastIndexOf('.');
        if (cutPoint < 100) cutPoint = remaining.substring(0, 500).lastIndexOf(' ');
        if (cutPoint < 100) cutPoint = 500;
        
        chunks.push(remaining.substring(0, cutPoint + 1).trim());
        remaining = remaining.substring(cutPoint + 1).trim();
      }
      
      // Play chunks sequentially
      for (let i = 0; i < chunks.length; i++) {
        if (!audioRef.current && i > 0) break; // Stop if user stopped playback
        
        try {
          const response = await axios.post(`${API_URL}/api/text-to-speech`, {
            text: chunks[i]
          });
          
          if (response.data.audio_base64) {
            const audio = new Audio(`data:audio/mp3;base64,${response.data.audio_base64}`);
            audioRef.current = audio;
            
            // Wait for this chunk to finish before playing next
            await new Promise((resolve) => {
              audio.onended = resolve;
              audio.onerror = resolve;
              audio.play().catch(resolve);
            });
          }
        } catch (err) {
          console.error('TTS chunk error:', err);
        }
      }
      
      setTtsPlaying(false);
      audioRef.current = null;
      
    } catch (error) {
      console.error('TTS error:', error);
      setTtsPlaying(false);
      audioRef.current = null;
    }
  };
  
  // Manual replay of last AI message
  const replayLastMessage = () => {
    const lastAssistantMsg = messages.filter(m => m.role === 'assistant').pop();
    if (lastAssistantMsg?.content) {
      playTTS(lastAssistantMsg.content);
    }
  };

  // Listen for playTTS events from child components
  useEffect(() => {
    const handlePlayTTS = (event) => {
      if (event.detail) {
        stopTTS(); // Stop any current playback
        setTimeout(() => playTTS(event.detail), 100);
      }
    };
    
    window.addEventListener('playTTS', handlePlayTTS);
    return () => window.removeEventListener('playTTS', handlePlayTTS);
  }, [ttsEnabled]);

  // Stop TTS playback
  const stopTTS = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setTtsPlaying(false);
    }
  };

  const sendMessage = async (text = input) => {
    if (!text.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setShowCategories(false);

    try {
      const response = await axios.post(`${API_URL}/api/chat`, {
        message: text,
        category: selectedCategory,
        session_id: sessionId,
        location_slug: selectedLocation
      });

      const aiResponse = response.data.response;
      
      // Start TTS IMMEDIATELY - don't wait for state updates
      // This runs in parallel with the UI updates below
      if (aiResponse && ttsEnabled) {
        playTTS(aiResponse); // Fire and forget - no await
      }

      const assistantMessage = {
        id: response.data.message_id,
        role: 'assistant',
        content: aiResponse,
        created_at: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      setSessionId(response.data.session_id);
      fetchSessions(); // This also runs in background
    } catch (error) {
      toast.error('Kon bericht niet verzenden');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = async (categoryId, subcategoryId = null) => {
    const cat = UI_CATEGORIES.find(c => c.id === categoryId);
    
    // Als categorie subcategorieën heeft en er is nog geen subcategorie geselecteerd
    if (cat?.subcategories?.length > 0 && !subcategoryId) {
      setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
      return;
    }
    
    const effectiveCategory = subcategoryId || categoryId;
    const categoryName = subcategoryId 
      ? cat?.subcategories?.find(s => s.id === subcategoryId)?.name || effectiveCategory
      : cat?.name || categoryId;
    
    setSelectedCategory(categoryId);
    
    // SNELLE LOKALE RESPONSE - geen API call nodig voor welkomstbericht
    setMessages([]);
    const newSessionId = crypto.randomUUID();
    setSessionId(newSessionId);
    
    // AI welkomstbericht per categorie - DIRECT LADEN (geen API call)
    const aiGreetings = {
          'inwerken': `Hé! 👋 Welkom! Ik ben Ilias, je digitale collega.

**Wat voor dienst ga je draaien?**
- 🌅 **Ochtenddienst** (08:00-15:00)
- 🌆 **Avonddienst** (15:00-23:00)  
- **Nachtdienst** (23:00-08:00)

Of kies een onderwerp:
- **Complete checklist** - Wat moet ik allemaal doen?
- **Medicatie** - Hoe werkt dat hier?
- **Huisafspraken** - Tijden en regels
- 📱 **Systemen** - Zilliz, Doctor Sillo, etc.

Ik help je door je hele dienst!`,

          'ochtenddienst': `Goedemorgen! Dit is je **ochtenddienst checklist**:

### Bij Aankomst (vóór 08:00)
- [ ] Leefomgeving controleren (schoon?)
- [ ] Groepsmail inloggen
- [ ] Groepstelefoon aan
- [ ] Overdracht lezen in Doctor Sillo

### 08:00 - Medicatie & Keuken
- [ ] Bewoners wakker maken, ramen open
- [ ] **Medicatie geven** (5 R's!)
- [ ] Voorraad tellen en noteren
- [ ] Koelkast/vriezer temperatuur checken
- [ ] Houdbaarheid boodschappen checken

### 08:00-09:30 - Ontbijt
- [ ] Ontbijt klaarzetten
- [ ] Bewoners aan tafel

### Na Ontbijt - Contactmoment
- [ ] ADL stimuleren (douchen, tandenpoetsen, kleding)
- [ ] Doelen bespreken (zorgplan)

**Waar wil je meer over weten?**`,

          'avonddienst': `Goedemiddag! 🌆 Dit is je **avonddienst checklist**:

### Bij Start
- [ ] Overdracht lezen
- [ ] Check wat er vandaag speelde

### 17:00 - Avondeten (VERPLICHT)
- [ ] Kijken wie mee kan koken
- [ ] Eten klaarmaken
- [ ] Iedereen aan tafel

### Na Avondeten
- [ ] **Medicatie geven** (5 R's!)
- [ ] Voorraad noteren
- [ ] Baxterol in retour box

### Dagbespreking
- [ ] Hoe is de dag gegaan?
- [ ] Aan doelen gewerkt?
- [ ] **Fishes uitdelen** bij positieve dag

### Afsluiten
- [ ] Overdracht schrijven (vóór 20:00)
- [ ] Rapportage maken (vóór 23:00)

**Waar wil je meer over weten?**`,

          'nachtdienst': `Goedeavond! Je begint aan de **nachtdienst**.

### Belangrijke taken:
- Regelmatig **rondes** maken
- **Achterwacht** bellen bij problemen
- Ochtend voorbereiden

### Noodgevallen:
- **112** bij spoed
- **Achterwacht** bij twijfel (nummer in Doctor Sillo)

### Overdracht ochtend:
- Wat is er 's nachts gebeurd?
- Bijzonderheden melden

**Waar kan ik je mee helpen?**`,

          'medicatie': `Hallo! **Medicatie Protocol**

### Wanneer?
- **Ochtend:** Na wekken, vóór ontbijt
- **Avond:** Na avondeten

### De 5 R's (VERPLICHT):
**Juiste Resident** - Klopt de naam?
**Juiste Medicatie** - Klopt het middel?
**Juiste Dosis** - Klopt de hoeveelheid?
**Juiste Tijdstip** - Is het nu?
**Juiste Toedieningsweg** - Hoe innemen?

### Niet vergeten:
- **Voorraad tellen** en noteren
- **Baxterol** → RETOUR BOX

### Contacten:
- **Apotheek:** Piushaven
- **Huisarts:** Poyraz

**Waar gaat je vraag over?**`,

          '5rs': `De **5 R's** zijn VERPLICHT bij elke medicatiegift!

| # | R | Vraag |
|---|---|-------|
| 1 | **Resident** | Is dit de juiste bewoner? |
| 2 | **Medicatie** | Klopt het medicijn? |
| 3 | **Dosis** | Is de hoeveelheid correct? |
| 4 | **Tijdstip** | Is het de juiste tijd? |
| 5 | **Toedieningsweg** | Oraal? Injectie? Anders? |

### Na toediening:
- Parafeer de aftekenlijst
- Baxterol in **RETOUR BOX**
- Noteer in voorraadlijst

**Wil je oefenen of heb je een vraag?**`,

          'clienten': `Hallo! Over welke cliënt wil je informatie?

### Groep Zwaluw - Enschotsestraat 98
- **Yassin** - Kamer 1
- **Mahad** - Kamer 2 (voorzitter)

### Groep Zwaluw - Enschotsestraat 100
- **Sietse** - Kamer 1
- **Richard** - Kamer 2
- **Axcel** - Kamer 4

**Wat wil je weten?**
- Zorgdoelen
- Medicatie
- Contactpersonen
- Signaleringsplan`,

          'huisafspraken': `Hallo! **Huisafspraken**

### Maaltijden (VERPLICHT)
| Maaltijd | Tijd |
|----------|------|
| Ontbijt | 08:00 - 09:30 |
| Lunch | 12:00 - 13:00 |
| Avondeten | **17:00** |

### Boodschappen
- Via **JUMBO app**
- Lijst samen met bewoners
- Begeleider is leidend

### Financieel
- **ING bankpas** voor aankopen
- Bonnetjes naar: **crediteuren@zorg-vleugel.nl**

### Fishes (Beloning)
- Positieve dag = fishes
- Bewaren in eigen kistje

**Waar gaat je vraag over?**`,

          'veiligheid': `🛡️ **Veiligheid & Noodgevallen**

### Bij SPOED: Bel 112

### Achterwacht Bellen
Wanneer:
- Medisch noodgeval
- Agressie/onveilig
- Cliënt vermist
- Twijfel

**Nummer:** Zie Doctor Sillo

### MIC Melding
Bij ELK incident:
1. MIC formulier invullen
2. Herstelgesprek voeren
3. Leidinggevende informeren

**Wat is er aan de hand?**`,

          'noodgevallen': `**NOODGEVALLEN**

### SPOED? BEL 112

### Wat is er aan de hand?
- **Medisch** - Bewusteloos, pijn, etc.
- **Agressie** - Dreigende situatie
- **Brand** - Alarm, evacuatie
- **Vermissing** - Cliënt weg

### Achterwacht
Bij twijfel ALTIJD bellen!
Nummer in Doctor Sillo.

**Beschrijf de situatie, ik help direct!**`,

          'brand': `**BRANDPROTOCOL**

### Bij brand - Stappen:
| Stap | Actie |
|------|-------|
| 1 | **ALARM** - Druk op brandmelder (rode knop) |
| 2 | **BEL 112** - Meld locatie en situatie |
| 3 | **WAARSCHUW** - Begeleiders, waakdienst, bewoners |
| 4 | **EVACUEER** - Volg groene vluchtpijlen |
| 5 | **VERZAMEL** - Meld je op verzamelplaats |

---

### 🏢 Onze Locaties & Verzamelplaatsen:

| Locatie | Adres | Verzamelplaats |
|---------|-------|----------------|
| **Groep Adelaars** | Ardennenlaan 68 | BUITEN, links van hoofdingang |
| **Groep Zwaluw** | Enschotsestraat 68 | BUITEN in de TUIN (bij Poort) |
| **Groep Zwaluw** | Enschotsestraat 98 | BUITEN bij de TUIN (achter gebouw) |
| **Groep Zwaluw** | Enschotsestraat 100 | BUITEN, naast hoofdingang |

### Na evacuatie:
Tel of alle bewoners aanwezig zijn
Wacht op instructies hulpdiensten
MIC melding invullen

---

**Op welke locatie ben je nu?** Vertel me het adres en ik geef je:
- De exacte vluchtroutes
- Locaties van brandblussers
- Specifieke instructies voor jouw verdieping`,

          'protocollen': `**Protocollen**

### Rapportage (SOEP)
- **S** - Subjectief (wat zegt cliënt?)
- **O** - Objectief (wat zie je?)
- **E** - Evaluatie (conclusie)
- **P** - Plan (vervolgactie)

**Deadline:** Vóór 23:00
**Tip:** Gebruik ChatGPT!

### Overdracht
**Deadline:** Vóór 20:00
Via Doctor Sillo

### MIC Melding
Bij ELK incident invullen!

**Welk protocol zoek je?**`,

          'dagritme': `⏰ **Dagritme**

| Tijd | Activiteit |
|------|------------|
| 08:00 | Dienst start, medicatie |
| 08:00-09:30 | Ontbijt |
| 12:00-13:00 | Lunch |
| 12:30 (ma) | Dagbesteding |
| 17:00 | Avondeten (VERPLICHT) |
| 20:00 | Overdracht deadline |
| 23:00 | Rapportage deadline |

### Maandag Dagbesteding
- 12:30 Groepslunch & spelletjes
- 14:00 Fitness & kickboksen
- 15:15 Drinken kopen & terug

**Waar wil je meer over weten?**`,

          'corvee': `🧹 **Corvee & Poetsdagen**

Staat op de lijst in de woning!

### Begeleider taken:
- Herinneren aan taken
- Aansturen op uitvoering
- Controleren

### Bij niet nakomen:
- Gesprek voeren
- Consequenties bespreken

**Wil je het schema zien of heb je een vraag?**`,

          'afval': `🗑️ **Afvalkalender**

Restafval: **Om de 2 weken**
Container **avond ervoor** buiten!

Check de **Dagritme tab** voor alle data.

**Heb je een vraag over het afval?**`,

          'rapportage': `**Rapportage**

### Deadline: Vóór 23:00

### SOEP Methode:
- **S** - Subjectief: Wat zegt de cliënt?
- **O** - Objectief: Wat observeer je?
- **E** - Evaluatie: Wat is je conclusie?
- **P** - Plan: Wat is de vervolgactie?

### Tips:
- Gebruik **ChatGPT** om te schrijven
- Kort maar volledig
- Bijzonderheden altijd vermelden

**Hulp nodig bij het schrijven?**`,

          'organisatie': `🏢 **Organisatie**

### Maandelijkse taken:
- **PB gesprek** - Met elke cliënt en hun PB
- **Bewonersvergadering** - Met alle bewoners
- **Begeleidingsoverleg & MDO** - Data via groepsapp

### Systemen:
- **Zilliz** - Dossiers, gesprekken
- **Doctor Sillo** - Medicatie, overdracht
- **Zorgly** - Beleid

### Contact:
- **Apotheek:** Piushaven
- **Huisarts:** Poyraz
- **Bonnetjes:** crediteuren@zorg-vleugel.nl

**Waar zoek je informatie over?**`,

          'agressie': `**Agressieprotocol - Zorg-Vleugel**

### Veiligheid Eerst!
Bij **direct gevaar** → **BEL 112**

---

### Signalen van Opbouwende Spanning
- Gespannen houding, rusteloze bewegingen
- Stemverheffing, snauwen
- Ballen van vuisten
- Starende blik

---

### De-escalatie - Wat doe je?
| Stap | Actie |
|------|-------|
| 1 | **Blijf kalm** - Adem rustig |
| 2 | **Afstand** - Minimaal 1,5 meter |
| 3 | **Zachte stem** - Rustig praten |
| 4 | **Erken emoties** - "Ik zie dat je boos bent" |
| 5 | **Open vragen** - "Wat kan ik doen?" |

---

### Bij Fysieke Agressie
1. **Veiligheid eerst** - Breng jezelf in veiligheid
2. **Waarschuw collega's**
3. **BEL 112** bij levensgevaar
4. **MIC melding** na afloop
5. **Gedragswetenschapper (Pelin)** informeren

---

### Nazorg & Analyse
- Bespreek met **gedragswetenschapper (Pelin)** voor gedragsanalyse
- Bij trauma: verwijs naar **GZ Psycholoog (Carella)**
- Evaluatie met **zorgmanager (Milenie)**

**Wat wil je weten?**
- De-escalatietechnieken
- Interventieniveaus
- Nazorg na incident`,

          'alcohol_drugs': `🚫 **Beleid Alcohol & Drugs**

### Regels Alcohol
| Waar | Regel |
|------|-------|
| Gemeenschappelijk | **Verboden** |
| Onder invloed | Naar eigen kamer |
| Eigen kamer | Bespreken in zorgplan |

### Regels Drugs
| Type | Regel |
|------|-------|
| **Harddrugs** | ALTIJD VERBODEN |
| **Softdrugs** | Niet toegestaan (tenzij medische indicatie) |
| **Handel** | Politie inschakelen |

### Bij Vermoeden
1. Signaleer het gebruik
2. Bespreek in teamoverleg
3. Pas zorgplan aan

### Bezoekers
- Geen alcohol/drugs meenemen
- Niet onder invloed komen
- Bij overtreding: wegsturen

**Waar gaat je vraag over?**`,

          'meldcode': `🛡️ **Meldcode Huiselijk Geweld & Kindermishandeling**

### Wat valt hieronder?
- Lichamelijke mishandeling
- Psychische mishandeling
- Seksueel misbruik
- Verwaarlozing
- Getuige van geweld

---

### 5 Stappen
| Stap | Actie |
|------|-------|
| 1 | **Signalen noteren** in Zilliz |
| 2 | **Overleg** met collega of Veilig Thuis |
| 3 | **Praat met cliënt** (als veilig!) |
| 4 | **Weeg risico** - hoe ernstig? |
| 5 | **Hulp of melden** |

---

### Belangrijke Contacten
| Contact | Naam |
|---------|--------|
| **Veilig Thuis** | **0800-2000** |
| Zorgmanager & Kwaliteitsmanager | Milenie |
| Gedragswetenschapper | Pelin |
| GZ Psycholoog | Carella |

**Direct gevaar? BEL 112!**

**Wat wil je weten?**`,

          'mic_melding': `**MIC Melding (Meld Incidenten Cliënten)**

### Wanneer MIC Melding?
Bij **ELK** incident:
- Agressie
- Medicatiefout
- Valincident
- Vermissing
- Overig

### Stappen
1. **Vul MIC formulier in** (via Zilliz/Zorgly)
2. **Herstelgesprek** voeren met betrokkenen
3. **Leidinggevende informeren**
4. **Bespreek in teamoverleg**

**Heb je een vraag over MIC meldingen?**`,

          'vrijheidsbeperkende': `🔒 **Vrijheidsbeperkende Maatregelen**

### Wat is vrijheidsbeperking?
Elke maatregel die de vrijheid van een cliënt beperkt:
- Deuren op slot
- Camera's
- Medicatie ter kalmering
- Fysieke interventie

---

### Wanneer toegestaan?
| Voorwaarde | Vereist |
|------------|---------|
| **Gevaar** | Acuut gevaar voor cliënt of anderen |
| **Toestemming** | Schriftelijke toestemming of wettelijk vertegenwoordiger |
| **Zorgplan** | Vastgelegd in zorgplan |
| **Evaluatie** | Regelmatig evalueren en afbouwen |

---

### Altijd doen:
1. **Rapporteren** in dossier
2. **Bespreken** in teamoverleg
3. **MIC melding** bij incident
4. **Evalueren** binnen 24 uur

**Wat wil je weten over vrijheidsbeperking?**`,

          'suicidaliteit': `**Protocol Suïcidaliteit**

### BIJ DIRECT GEVAAR → BEL 112

---

### Signalen herkennen:
- Uitspraken over "niet meer willen leven"
- Terugtrekken, afscheid nemen
- Plotselinge rust na periode van somberheid
- Weggeven van bezittingen

---

### Wat doe je?
| Stap | Actie |
|------|-------|
| 1 | **Vraag direct** - "Heb je gedachten aan zelfdoding?" |
| 2 | **Luister** - Niet bagatelliseren |
| 3 | **Niet alleen laten** |
| 4 | **Achterwacht bellen** |
| 5 | **Rapporteren** |

---

### Hulplijnen:
| Dienst | Nummer |
|--------|--------|
| **113 Zelfmoordpreventie** | 0900-0113 |
| **Crisislijn GGZ** | Via achterwacht |
| **GZ Psycholoog** | Carella (werkdagen) |

---

### Nazorg & Evaluatie:
- Bespreek met **GZ Psycholoog (Carella)**
- Gedragsanalyse door **gedragswetenschapper (Pelin)**
- Evaluatie met **zorgmanager (Milenie)**

**Maak je je zorgen om een cliënt?**`,

          'medicatie_incidenten': `**Medicatie Incidenten**

### Wanneer MIC melding?
Bij ELKE medicatiefout:
- Verkeerde medicatie gegeven
- Verkeerde dosis
- Verkeerde tijd
- Medicatie vergeten
- Medicatie geweigerd

---

### Direct handelen:
| Situatie | Actie |
|----------|-------|
| **Te veel gegeven** | Arts/apotheek bellen, symptomen monitoren |
| **Vergeten** | NIET inhalen zonder overleg arts |
| **Verkeerd medicijn** | Arts bellen, symptomen monitoren |

---

### Altijd doen:
1. **MIC melding** invullen
2. **Arts/apotheek** informeren
3. **Zorgmanager (Milenie)** informeren
4. **Rapporteren** in dossier

**Wat is er gebeurd?**`,

          'valpreventie': `🚶 **Valpreventie Protocol**

### Risicofactoren:
- Medicatie (slaap, kalmerend)
- Mobiliteit
- Omgeving (gladde vloeren, drempels)
- Schoeisel
- Verwardheid

---

### Preventie:
| Maatregel | Actie |
|-----------|-------|
| **Omgeving** | Verlichting, geen obstakels, antislipmatten |
| **Hulpmiddelen** | Rollator, wandelstok beschikbaar |
| **ADL** | Begeleiden bij verplaatsen |
| **Nacht** | Nachtlampje, bed laag |

---

### Bij een val:
1. **Controleer** verwondingen
2. **Niet optillen** bij twijfel
3. **Arts bellen** bij hoofdletsel of bewustzijnsverlies
4. **MIC melding** invullen
5. **Rapporteren**

**Wil je meer weten over valpreventie?**`,

          'hygieneprotocol': `🧼 **Hygiëneprotocol**

### Handhygiëne - 5 momenten:
1. **Voor** cliëntcontact
2. **Voor** schone handeling
3. **Na** blootstelling aan lichaamsvloeistoffen
4. **Na** cliëntcontact
5. **Na** aanraken omgeving cliënt

---

### Persoonlijke beschermingsmiddelen:
| Situatie | Gebruik |
|----------|---------|
| **Verzorging** | Handschoenen, schort |
| **Wondverzorging** | Handschoenen, steriel |
| **Besmettelijke ziekte** | Mondkapje, handschoenen, schort |

---

### Schoonmaak:
- Dagelijks: sanitair, keuken
- Wekelijks: vloeren, oppervlakken
- Direct: bij morsen lichaamsvloeistoffen

**Waar heb je hulp bij nodig?**`,

          'achterwacht': `📱 **Achterwacht Bellen**

### Wanneer bellen?
- **Medisch noodgeval** (na/naast 112)
- **Agressie** of onveilige situatie
- **Cliënt vermist**
- **Twijfel** over handelen
- **Ernstig incident**

---

### Hoe bereiken?
| Methode | Info |
|---------|------|
| **Telefoonnummer** | Zie Doctor Sillo |
| **Bereikbaar** | 24/7 |

---

### Wat vertellen?
1. Wie je bent
2. Waar je bent
3. Wat er aan de hand is
4. Wat je al gedaan hebt
5. Wat je nodig hebt

**Heb je nu de achterwacht nodig?**`,

          'overdracht_protocol': `🔄 **Overdrachtsprotocol**

### Deadline: Vóór 20:00

### Wat moet erin?
- Bijzonderheden per cliënt
- Medicatie afwijkingen
- Afspraken en bezoek
- Taken voor volgende dienst

---

### Waar invullen?
**Doctor Sillo** - Overdracht module

### Tips:
- Kort maar volledig
- Concreet en feitelijk
- Actiepunten duidelijk

**Hulp nodig bij de overdracht?**`,

          'calamiteiten': `**Calamiteitenplan Begeleid Wonen**

### Bij noodsituatie (brand, agressie, etc.):
1. **ALARM** - Waarschuw alle aanwezigen
2. **EVACUEER** - Breng cliënten naar verzamelplaats
3. **BEL 112** - Geef locatie en situatie door
4. **ACHTERWACHT** - Bel direct de achterwacht
5. **REGISTREER** - Wie is waar?

---

### Verzamelplaatsen:
| Locatie | Verzamelplaats |
|---------|----------------|
| Ardennenlaan 68 | Parkeerplaats achter |
| Enschotsestraat 68 | Speeltuin |
| Enschotsestraat 98 | Parkeerplaats |
| Enschotsestraat 100 | Speeltuin |

---

### Jaarlijkse Ontruimingsoefening:
- Minstens 1x per jaar
- Evaluatieformulier invullen (PRB17)
- Bespreken in teamoverleg

**Wil je een ontruimingsoefening starten?**`,

          'seksueel_gedrag': `⛔ **Seksueel Grensoverschrijdend Gedrag (SGOG)**

### Vormen:
- Ongewenste aanrakingen
- Seksueel getinte opmerkingen
- Misbruik van machtspositie
- Online seksueel gedrag

---

### Wat doe je?
| Stap | Actie |
|------|-------|
| 1 | **STOP** - Beëindig de situatie |
| 2 | **Veiligheid** - Bescherm het slachtoffer |
| 3 | **Meld** - Gedragswetenschapper (Pelin) informeren |
| 4 | **Rapporteer** - MIC melding invullen |
| 5 | **Nazorg** - GZ Psycholoog (Carella) inschakelen |

---

### Contactpersonen SGOG:
- **Gedragswetenschapper:** Pelin
- **GZ Psycholoog:** Carella
- **Zorgmanager:** Milenie

### Bij vermoedens bij personeel:
- Direct melden bij zorgmanager (Milenie)
- Vergewisplicht uitvoeren
- VOG controleren

**Maak je je zorgen over een situatie?**`,

          'gedragscode': `📖 **Gedragscode**

### Kernwaarden:
- **Respect** - Voor cliënten en collega's
- **Integriteit** - Eerlijk en betrouwbaar
- **Professionaliteit** - Grenzen bewaken
- **Veiligheid** - Altijd prioriteit

---

### Omgang met Cliënten:
| Wel | Niet |
|-----|------|
| Professionele afstand | Privérelaties |
| Respectvolle bejegening | Schelden/kleineren |
| Privacy respecteren | Informatie delen zonder toestemming |
| Autonomie stimuleren | Betuttelen |

---

### Sociale Media:
- Geen cliëntinfo delen
- Geen privécontact met cliënten
- Professioneel blijven

**Vragen over de gedragscode?**`,

          'aanmelding_uitstroom': `🔄 **Aanmelding tot Uitstroom**

### Proces:
| Fase | Duur | Activiteiten |
|------|------|--------------|
| **Aanmelding** | - | Aanmeldformulier, screening |
| **Intake** | 1-2 weken | Intakegesprek, risico-inventarisatie |
| **Instroom** | 6 weken | Kennismaking, zorgplan opstellen |
| **Uitvoering** | Variabel | Begeleiding, evaluaties |
| **Uitstroom** | 4-6 weken | Afbouwen, eindverslag |

---

### Documenten per fase:
- **Intake:** PRB01, PRB02, PRB03
- **Zorgplan:** PRB08A, PRB08B
- **Uitstroom:** PRB10

**Hulp nodig met een specifieke fase?**`,

          'intake_proces': `**Intake Proces**

### Stappen:
1. **Aanmelding ontvangen** - Via verwijzer of gemeente
2. **Screening** - Past cliënt bij ons aanbod?
3. **Intakegesprek** - Met cliënt en netwerk
4. **Risico-inventarisatie** - PRB03 invullen
5. **Besluit** - Wel/niet plaatsen

---

### Documenten:
| Document | Doel |
|----------|------|
| PRB01 | Aanmeldformulier |
| PRB02 | Intakeformulier |
| PRB03 | Risico-inventarisatie |
| PRB05 | AVG toestemming |

**Hulp nodig bij de intake?**`,

          'zorgplan': `📑 **Zorgplan (PRB08A)**

### Onderdelen:
1. **Persoonlijke gegevens** - NAW, contacten
2. **Hulpvraag** - Wat wil de cliënt bereiken?
3. **Doelen** - SMART geformuleerd
4. **Acties** - Wie doet wat?
5. **Evaluatiemomenten** - Wanneer evalueren?

---

### Tips:
- Stel doelen **samen** met de cliënt
- Gebruik **Krachtwerk methodiek**
- Evalueer minimaal **elke 6 maanden**

**Hulp nodig bij het opstellen van een zorgplan?**`,

          'signaleringsplan': `**Signaleringsplan (PRB21)**

### Fasen:
| Fase | Kenmerken | Actie |
|------|-----------|-------|
| 🟢 **Groen** | Stabiel, ontspannen | Onderhouden |
| 🟠 **Oranje** | Spanning, triggers | Preventief handelen |
| 🔴 **Rood** | Crisis | Direct ingrijpen |

---

### Opstellen:
1. Bespreek met cliënt en GW
2. Identificeer signalen per fase
3. Bepaal acties per fase
4. Deel met team

**Wil je een signaleringsplan opstellen of aanpassen?**`,

          'krachtwerk': `💪 **Krachtwerk Methodiek**

### Principes:
- **Krachten centraal** - Niet problemen
- **Eigen regie** - Cliënt bepaalt
- **Sociale netwerk** - Betrek omgeving
- **Hoop en perspectief** - Positief kijken

---

### In de praktijk:
| Stap | Vraag |
|------|-------|
| 1 | Wat gaat al goed? |
| 2 | Wat wil je bereiken? |
| 3 | Wie kan je helpen? |
| 4 | Welke krachten heb je? |

**Hulp nodig met Krachtwerk?**`,

          'fasewerken': `📈 **Werken met Fases**

### Fasen naar zelfstandigheid:
| Fase | Focus | Begeleiding |
|------|-------|-------------|
| **1** | Wennen, stabiliteit | Intensief |
| **2** | Doelen werken | Regelmatig |
| **3** | Zelfstandigheid | Afbouwend |
| **4** | Uitstroom | Minimaal |

---

### Doorstromen:
- Evalueer samen met cliënt
- Leg vast in zorgplan
- Vier successen!

**Hulp nodig met fase-indeling?**`,

          'beloningssysteem': `**Beloningssysteem**

### Hoe werkt het?
- Cliënten verdienen punten voor positief gedrag
- Punten kunnen ingewisseld worden
- Focus op belonen, niet straffen

---

### Beloningskaart (PRB11):
| Gedrag | Punten |
|--------|--------|
| Op tijd bij afspraak | +1 |
| Corvee gedaan | +1 |
| Medicatie op tijd | +1 |
| Positieve houding | +1 |

---

### Tips:
- Wees consistent
- Beloon direct
- Leg uit waarom

**Hulp nodig met het beloningssysteem?**`,

          'werving_selectie': `🔍 **Werving & Selectie**

### Proces:
| Stap | Actie |
|------|-------|
| 1 | Vacature opstellen |
| 2 | Selectie op CV |
| 3 | Sollicitatiegesprek |
| 4 | Referenties checken |
| 5 | VOG aanvragen |
| 6 | Arbeidsovereenkomst |

---

### Vergewisplicht:
- [ ] VOG Verklaring
- [ ] Diploma's controleren
- [ ] Referenties bellen
- [ ] BIG/SKJ register checken

**Vragen over werving?**`,

          'inwerkprocedure': `📚 **Inwerkprocedure**

### Eerste Week:
| Dag | Activiteit |
|-----|------------|
| 1 | Rondleiding, systeemtoegang |
| 2 | Kennismaking team |
| 3 | Introductie cliënten |
| 4 | Meelopen met buddy |
| 5 | Evaluatie eerste week |

---

### Documenten:
- PE01 Informatieboekje
- PE03 Opdrachtovereenkomst
- PE04 Verwerkersovereenkomst

**Nieuwe medewerker inwerken?**`,

          'functioneringsgesprek': `💬 **Functioneringsgesprek (PE05)**

### Onderwerpen:
- Werkzaamheden en resultaten
- Samenwerking
- Ontwikkeling
- Werkbalans
- Verwachtingen

---

### Frequentie:
| Functie | Frequentie |
|---------|------------|
| Medewerker | Jaarlijks |
| Stagiaire | Wekelijks |
| Vrijwilliger | Tweewekelijks |

**Hulp bij het voorbereiden?**`,

          'beoordelingsgesprek': `**Beoordelingsgesprek (PE06)**

### Onderwerpen:
- Prestaties afgelopen periode
- Gedrag en houding
- Samenwerking
- Ontwikkeling
- Salaris/contractverlenging

---

### Beoordeling:
| Score | Betekenis |
|-------|-----------|
| **A** | Excellent |
| **B** | Goed |
| **C** | Voldoende |
| **D** | Onvoldoende |

**Hulp nodig met het beoordelingsgesprek?**`,

          'personeelsdossier': `📁 **Personeelsdossier (PE08)**

### Bevat:
- Arbeidsovereenkomst
- VOG
- Diploma's/certificaten
- Functioneringsverslagen
- Bijscholingsregistratie

---

### Bewaartermijn:
- Tijdens dienstverband + 7 jaar
- AVG: alleen noodzakelijke gegevens

**Vragen over het personeelsdossier?**`,

          'bijscholing': `🎓 **Bijscholing & Ontwikkeling (PE09)**

### Verplichte trainingen:
- Medicatie
- BHV/EHBO (jaarlijks)
- Agressiehantering
- Meldcode

---

### Registratie:
- In personeelsdossier
- PE09 Scholingsregistratie

**Welke bijscholing zoek je?**`,

          'arbo_rie': `🛡️ **Arbo & RIE**

### Risico Inventarisatie & Evaluatie:
- Jaarlijks uitvoeren
- Fysieke belasting
- Psychosociale arbeidsbelasting
- Agressie risico's

---

### Acties:
| Risico | Maatregel |
|--------|-----------|
| Tillen | Hulpmiddelen gebruiken |
| Stress | Werkdruk monitoren |
| Agressie | Training, protocol |

**Vragen over Arbo?**`,

          'bhv_ehbo': `🏥 **BHV & EHBO**

### BHV'ers per locatie:
- Minimaal 1 BHV'er aanwezig
- Jaarlijkse herhalingscursus

---

### Bij noodgeval:
1. **Alarmeer** - Waarschuw anderen
2. **Help** - Eerste hulp verlenen
3. **Evacueer** - Naar verzamelplaats
4. **Bel 112** - Professionele hulp

**Wie zijn de BHV'ers vandaag?**`,

          'nazorg_incident': `🤝 **Nazorg bij Incidenten**

### Na een incident:
| Wanneer | Actie |
|---------|-------|
| Direct | Veiligheid, eerste hulp |
| < 24 uur | Gesprek met betrokkenen |
| < 1 week | Teambespreking |
| 4-6 weken | Evaluatiegesprek |

---

### Ondersteuning:
- Gesprek met zorgmanager (Milenie)
- Bedrijfsmaatschappelijk werk
- Vertrouwenspersoon
- GZ Psycholoog (Carella) bij complexe situaties

**Heb je ondersteuning nodig?**`,

          'medezeggenschap': `🗳️ **Medezeggenschap (PVT/OR)**

### Personeelsvertegenwoordiging:
- Adviesrecht bij beleid
- Instemmingsrecht bij arbeidsvoorwaarden
- Overleg met directie

---

### Onderwerpen:
- Roosters
- Arbeidsomstandigheden
- Scholingsbeleid
- Reorganisaties

**Vragen over medezeggenschap?**`,

          'externe_audit': `**Externe Audit**

### Certificering:
- ISO 9001 (kwaliteit)
- HKZ (zorgsector)
- Jaarlijks controlebezoek

---

### Voorbereiding:
1. Documentatie actueel
2. Medewerkers informeren
3. Procesafwijkingen oplossen

**Hulp bij auditvoorbereiding?**`,

          'verbeterregister': `📈 **Verbeterregister (H09)**

### Registratie van:
- Klachten
- Incidenten
- Auditbevindingen
- Verbeteracties

---

### Status:
| Status | Betekenis |
|--------|-----------|
| Open | Actie vereist |
| In uitvoering | Wordt opgepakt |
| Afgerond | Opgelost |

**Wil je een verbetering melden?**`,

          'afwijkingen': `**Afwijkingen & Corrigerende Maatregelen**

### Bij afwijking:
1. **Signaleer** - Wat gaat niet goed?
2. **Analyseer** - Oorzaak bepalen
3. **Corrigeer** - Directe actie
4. **Preventie** - Herhaling voorkomen

---

### Documentatie:
- H09 Verbeterregister
- MIC melding (bij incident)

**Wil je een afwijking melden?**`,

          'klachten': `**Klachtenafhandeling (H11)**

### Procedure:
| Stap | Termijn |
|------|---------|
| Ontvangst | Bevestiging < 3 dagen |
| Onderzoek | < 4 weken |
| Afhandeling | < 6 weken |

---

### Klachtencommissie:
- Onafhankelijk
- Externe voorzitter
- Bindend advies

**Heb je een klacht ontvangen?**`,

          'tevredenheid': `😊 **Tevredenheidsonderzoek**

### Onderzoeken:
| Type | Frequentie |
|------|------------|
| Cliënttevredenheid | Jaarlijks |
| Medewerkertevredenheid | Jaarlijks |
| Verwantentevredenheid | 2-jaarlijks |

---

### Documenten:
- H13-H16 Tevredenheidslijsten

**Hulp bij tevredenheidsonderzoek?**`,

          'informatiebeveiliging': `🔒 **Informatiebeveiliging (NEN-7510)**

### Maatregelen:
- Sterke wachtwoorden
- Schermvergrendeling
- Versleutelde verbindingen
- Clean desk

---

### Bij datalek:
1. Direct melden aan zorgmanager (Milenie)
2. Registratie in H09
3. Autoriteit Persoonsgegevens (indien nodig)

**Vragen over informatiebeveiliging?**`,

          'wet_regelgeving': `⚖️ **Wet- en Regelgeving**

### Relevante wetten:
| Wet | Onderwerp |
|-----|-----------|
| **Wmo** | Begeleiding, ondersteuning |
| **Wkkgz** | Kwaliteit, klachten |
| **AVG** | Privacy |
| **Wmcz** | Medezeggenschap cliënten |

**Vragen over wet- en regelgeving?**`,

          'profielen': `👤 **Cliëntprofielen**

### Beschikbare informatie:
- Persoonlijke gegevens
- Diagnose
- Medicatie
- Contactpersonen
- Zorgdoelen

---

### Welke cliënt?
Typ de naam of initialen van de cliënt.

**Over welke cliënt wil je informatie?**`,

          'zorgdoelen': `🎯 **Zorgdoelen**

### SMART doelen:
| Letter | Betekenis |
|--------|-----------|
| **S** | Specifiek |
| **M** | Meetbaar |
| **A** | Acceptabel |
| **R** | Realistisch |
| **T** | Tijdgebonden |

---

### Voorbeelden:
- "Over 3 maanden kan cliënt zelfstandig koken"
- "Cliënt gaat 2x per week naar dagbesteding"

**Hulp bij het opstellen van doelen?**`,

          'contactpersonen': `📞 **Contactpersonen**

### Per cliënt:
- Familie/verwanten
- Wettelijk vertegenwoordiger
- Huisarts
- Specialist

---

### Bereikbaar via:
- Cliëntdossier in Zilliz
- Groepstelefoon contactenlijst

**Over welke cliënt zoek je contactgegevens?**`,

          'risico_inventarisatie': `**Risico-inventarisatie Cliënt (PRB03)**

### Risicogebieden:
| Gebied | Voorbeelden |
|--------|-------------|
| Gezondheid | Medicatie, vallen |
| Gedrag | Agressie, zelfbeschadiging |
| Sociaal | Isolatie, uitbuiting |
| Financieel | Schulden, bewindvoering |

---

### Bij hoog risico:
- Signaleringsplan opstellen
- Teambespreking
- Eventueel GW inschakelen

**Hulp bij risico-inventarisatie?**`,

          'groepsdynamiek': `**Groepsdynamiek**

### Aspecten:
- Onderlinge relaties cliënten
- Conflicten
- Groepsactiviteiten
- Sfeer in de groep

---

### Tips:
| Situatie | Aanpak |
|----------|--------|
| Conflict | Bemiddelen, apart spreken |
| Isolatie | Betrekken, activiteiten |
| Negatieve sfeer | Positief bekrachtigen |

**Vragen over de groep?**`,

          'client_participatie': `🗣️ **Cliëntparticipatie**

### Vormen:
- Huisvergaderingen
- Individuele gesprekken
- Cliëntenraad
- Exitgesprekken

---

### Onderwerpen:
- Dagritme
- Activiteiten
- Huisregels
- Verbeterpunten

**Hoe kan ik helpen?**`,

          'zak_kleedgeld': `**Zak- en Kleedgeld (PRB12)**

### Beheer:
| Type | Verantwoordelijke |
|------|-------------------|
| Eigen beheer | Cliënt |
| Gedeeld beheer | Cliënt + begeleider |
| Volledig beheer | Begeleider |

---

### Administratie:
- Kasboek bijhouden
- Bonnetjes bewaren
- Maandelijkse controle

**Hulp met financieel beheer?**`,

          'netwerk_client': `🌐 **Netwerk Cliënt**

### Betrokken partijen:
- Familie
- Vrienden
- Dagbesteding
- Behandelaren
- Bewindvoerder

---

### Netwerkgesprek:
- Minimaal 1x per jaar
- Evaluatie zorgplan
- Toekomstplannen

**Hulp bij netwerkbetrokkenheid?**`,

          'dubbele_controle': `🔒 **Dubbele Controle Medicatie**

### Wanneer verplicht?
- Risicovolle medicatie
- Insuline
- Opiaten
- Bij twijfel

---

### Procedure:
1. Eerste begeleider bereidt voor
2. Tweede begeleider controleert
3. Beiden tekenen af

**Hulp nodig met dubbele controle?**`,

          'vermissing': `**Medicatie Vermissing**

### Bij ontbrekende medicatie:
1. **Check** - Alle locaties controleren
2. **Vraag** - Cliënt en collega's
3. **Meld** - Apotheek bellen
4. **MIC** - Melding invullen

---

### Let op:
- Niet zelf "oplossen"
- Altijd rapporteren
- Arts informeren

**Is er medicatie vermist?**`,

          'zelfbeheer': `**Medicatie Zelfbeheer**

### Voorwaarden:
- Cliënt is wilsbekwaam
- Afspraken in zorgplan
- Periodieke evaluatie

---

### Niveaus:
| Niveau | Omschrijving |
|--------|--------------|
| Volledig | Cliënt beheert alles |
| Gedeeld | Samen met begeleider |
| Onder toezicht | Begeleider controleert |

**Vragen over zelfbeheer?**`,

          'medicatie_clienten': `👤 **Medicatie per Cliënt**

Typ de naam of initialen van de cliënt om de medicatie-informatie te zien.

### Beschikbare info:
- Huidige medicatie
- Dosering en tijden
- Type beheer
- Bijzonderheden

**Over welke cliënt wil je medicatie-info?**`,

          'systemen': `💻 **Systemen**

### Gebruikte systemen:
| Systeem | Doel |
|---------|------|
| **Zilliz/Doctor Sillo** | Rapportage, dossier |
| **Groepsmail** | Communicatie |
| **Zorgly** | Protocollen, beleid |
| **Aftekenlijst** | Medicatie |

---

### Inloggen:
- Vraag inloggegevens aan zorgmanager (Milenie)
- Gebruik sterke wachtwoorden

**Hulp met een systeem?**`,

          'overdracht': `**Overdracht Schrijven**

### Onderdelen:
- Bijzonderheden per cliënt
- Medicatie-afwijkingen
- Afspraken/bezoek
- Taken voor volgende dienst

---

### Tips:
- Kort maar volledig
- Feitelijk schrijven
- Deadline: vóór 20:00

**Hulp met de overdracht?**`,

          'boodschappen': `**Boodschappen**

### Wekelijks:
- Boodschappenlijst maken
- Budget: zie financieel overzicht
- Kassabon bewaren

---

### Tips:
- Cliënten betrekken
- Weekmenu als basis
- Let op houdbaarheidsdatums

**Hulp met boodschappen?**`,

          'financieel': `💵 **Financieel Beheer**

### Huishoudgeld:
- Boodschappen
- Huishoudelijke artikelen
- Activiteiten

---

### Administratie:
- Kassabon bewaren
- Kasboek bijhouden
- Maandafsluiting

**Hulp met financiën?**`,

          'personeel': `👔 **Personeelszaken**

Kies een onderwerp:
- 🔍 Werving & Selectie
- 📚 Inwerkprocedure
- 💬 Functioneringsgesprek
- Beoordelingsgesprek
- 📁 Personeelsdossier
- 🎓 Bijscholing
- 🛡️ Arbo & RIE
- 🏥 BHV & EHBO

**Waar kan ik je mee helpen?**`,

          'primair_proces': `🔄 **Primair Proces**

### Cliënttraject:
1. Aanmelding
2. Intake
3. Instroom
4. Begeleiding
5. Uitstroom

---

Kies een onderwerp voor meer informatie.

**Waar wil je meer over weten?**`,

          'kwaliteit': `**Kwaliteitssysteem**

### Onderdelen:
- Directiebeoordeling
- Interne/Externe Audit
- Verbeterregister
- Klachtenafhandeling
- Tevredenheidsonderzoek

---

Kies een onderwerp voor meer informatie.

**Waar kan ik je mee helpen?**`,

          'ochtend': `🌅 **Ochtend (08:00-12:00)**

Bekijk het takenlijstje links voor alle ochtendtaken.

**Snelle taken:**
- Medicatie geven (08:00)
- Ontbijt (09:00)
- Contactmoment 1 (09:30)
- ADL begeleiding

**Hulp nodig met een taak?**`,

          'middag': `**Middag (12:00-17:00)**

Bekijk het takenlijstje links voor alle middagtaken.

**Snelle taken:**
- Lunch (12:00)
- Contactmoment 2 (13:00)
- Middagactiviteiten
- Samen koken (16:00)

**Hulp nodig met een taak?**`,

          'avond': `🌆 **Avond (17:00-23:00)**

Bekijk het takenlijstje links voor alle avondtaken.

**Snelle taken:**
- Avondeten (17:00)
- Contactmoment 3 (17:45)
- Medicatie (18:30)
- Rapportage (22:00)

**Hulp nodig met een taak?**`,

          'nacht': `**Nacht (23:00-08:00)**

### Taken:
- Nachtrondes (23:30, 02:00, 05:00)
- Beschikbaar voor hulpvragen
- Overdracht schrijven (07:00)

**Hulp nodig vannacht?**`,

          'directiebeoordeling': `**Directiebeoordeling (H17)**

### Jaarlijkse evaluatie van:
- Status acties vorig jaar
- Klanttevredenheid
- Medewerkers tevredenheid
- Audit resultaten
- Kwaliteitsdoelstellingen

---

### Documenten:
- H17 Directiebeoordeling
- H09 Verbeterregister
- H13-H16 Tevredenheidslijsten

**Vragen over de directiebeoordeling?**`,

          'interne_audit': `🔍 **Interne Audit (H19)**

### Doel:
- Effectiviteit kwaliteitssysteem toetsen
- Voorbereiding externe audit
- Verbeterpunten identificeren

---

### Frequentie: Jaarlijks

### Onderdelen:
- Procesbeschrijvingen
- Documentatie
- Uitvoering in praktijk

**Hulp nodig bij de audit?**`,

          'avg_privacy': `🔐 **AVG & Privacy**

### Persoonsgegevens:
| Wel verwerken | Niet verwerken |
|---------------|----------------|
| Met toestemming | Zonder doel |
| In cliëntdossier | Delen zonder toestemming |
| Beveiligd | Onnodig bewaren |

---

### Bewaartermijnen:
- Cliëntdossier: 20 jaar
- Personeelsdossier: 7 jaar na uitdienst

**Vragen over privacy?**`,

          'overige': `Hé! 💬 Geen probleem, stel je vraag!

Ik ken alle protocollen, huisafspraken, cliëntinformatie en meer.

### Populaire onderwerpen:
- Medicatie & 5 J's
- Brandprotocol
- Agressieprotocol
- 🛡️ Meldcode Huiselijk Geweld
- 🚫 Alcohol & Drugs beleid
- 🔒 Vrijheidsbeperkende Maatregelen
- MIC Melding
- 📑 Zorgplan & Signaleringsplan
- 💪 Krachtwerk Methodiek

**Waar kan ik je mee helpen?**`
        };
        
        const greeting = aiGreetings[effectiveCategory] || aiGreetings['overige'];
        
        // Voeg AI bericht direct toe (geen wachten op API)
        setMessages([{
          role: 'assistant',
          content: greeting
        }]);
        
        // Sla sessie op achtergrond op (niet blokkeren)
        axios.post(`${API_URL}/api/chat`, {
          message: `[SYSTEM: Gebruiker opende categorie ${categoryName}]`,
          category: effectiveCategory,
          session_id: newSessionId
        }).catch(() => {});
        
        toast.success(`${categoryName}`, { duration: 1500 });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await sendAudioToTranscribe(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Opname gestart...');
    } catch (error) {
      toast.error('Kon microfoon niet gebruiken');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const sendAudioToTranscribe = async (audioBlob) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/speech-to-text`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.text) {
        setInput(response.data.text);
        toast.success('Spraak omgezet naar tekst');
      }
    } catch (error) {
      console.error('Speech-to-text error:', error);
      toast.error('Spraakherkenning mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/chat/sessions/${sid}`);
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) startNewChat();
      toast.success('Gesprek verwijderd');
    } catch (error) {
      toast.error('Kon gesprek niet verwijderen');
    }
  };

  // Location Picker - Vervangen door mooie GroupSelectionPage
  if (showLocationPicker) {
    return (
      <GroupSelectionPage
        user={user}
        onSelectGroup={(groupId) => {
          const loc = locations.find(l => l.slug === groupId || l.id === groupId) || { 
            id: groupId, 
            slug: groupId, 
            name: groupId === 'groep-zwaluw' ? 'Groep Zwaluw' : 'Groep Adelaars' 
          };
          selectLocation(loc);
        }}
      />
    );
  }

  // Early return for stagiaire dashboard
  if (isStagiaire) {
    return (
      <div className="h-screen flex bg-gray-50 overflow-hidden">
        <main className="flex-1 flex flex-col min-w-0">
          <header className="h-14 sm:h-16 border-b border-slate-200 bg-white flex items-center justify-between px-2 sm:px-4 shadow-sm">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <img src={LOGO_URL} alt="Zorg-Vleugel" className="w-8 h-8 sm:w-10 sm:h-10" />
                <span className="text-sm sm:text-lg font-bold text-[#1e3a5f]">Stagiaire Portal</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 hidden sm:inline">{user?.name}</span>
              <button
                onClick={logout}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </header>
          <div className="flex-1 overflow-auto bg-slate-50 p-2 sm:p-4 lg:p-6">
            <div className="max-w-4xl mx-auto">
              <StagiaireDashboard 
                user={user} 
                token={localStorage.getItem('token')}
                onOpenChat={() => setShowAiChat(true)}
              />
            </div>
          </div>
        </main>
        
        {/* AI Chat Modal */}
        <AIChatModal 
          isOpen={showAiChat}
          onClose={() => setShowAiChat(false)}
          user={user}
          messages={messages}
          setMessages={setMessages}
          input={input}
          setInput={setInput}
          loading={loading}
          sendMessage={sendMessage}
          isRecording={isRecording}
          startRecording={startRecording}
          stopRecording={stopRecording}
          ttsEnabled={ttsEnabled}
          setTtsEnabled={setTtsEnabled}
          ttsPlaying={ttsPlaying}
          stopTTS={stopTTS}
        />
      </div>
    );
  }

  // Early return for werkbegeleider dashboard
  if (isWerkbegeleider) {
    return (
      <div className="h-screen flex bg-gray-50 overflow-hidden">
        <main className="flex-1 flex flex-col min-w-0">
          <header className="h-14 sm:h-16 border-b border-slate-200 bg-white flex items-center justify-between px-2 sm:px-4 shadow-sm">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <img src={LOGO_URL} alt="Zorg-Vleugel" className="w-8 h-8 sm:w-10 sm:h-10" />
                <span className="text-sm sm:text-lg font-bold text-[#1e3a5f]">Werkbegeleider Portal</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 hidden sm:inline">{user?.name}</span>
              <button
                onClick={logout}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </header>
          <div className="flex-1 overflow-auto bg-slate-50 p-2 sm:p-4 lg:p-6">
            <div className="max-w-6xl mx-auto">
              <WerkbegeleiderDashboard 
                user={user} 
                token={localStorage.getItem('token')}
              />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // HomeScreen View - iPhone-style App Launcher
  if (activeView === 'home') {
    return (
      <>
        <HomeScreen
          user={user}
          selectedLocation={selectedLocation}
          onLocationChange={(newLocation) => setSelectedLocation(newLocation)}
          notifications={homeNotifications}
          onOpenAiChat={() => setShowAiChat(true)}
          onNavigateTo={(target) => {
            // Navigate to full-screen pages
            setActiveView(target);
          }}
          onOpenFullDashboard={() => setActiveView('dashboard')}
          onLogout={logout}
        />
        
        {/* AI Chat Modal - Also available from HomeScreen */}
        <AIChatModal 
          isOpen={showAiChat}
          onClose={() => setShowAiChat(false)}
          user={user}
          messages={messages}
          setMessages={setMessages}
          input={input}
          setInput={setInput}
          loading={loading}
          sendMessage={sendMessage}
          isRecording={isRecording}
          startRecording={startRecording}
          stopRecording={stopRecording}
          ttsEnabled={ttsEnabled}
          setTtsEnabled={setTtsEnabled}
          ttsPlaying={ttsPlaying}
          stopTTS={stopTTS}
        />
      </>
    );
  }

  // Full-screen Pages - Met Lazy Loading voor snellere navigatie
  
  // Category Pages - Gegroepeerde apps
  if (activeView?.startsWith('category-')) {
    const categoryId = activeView.replace('category-', '');
    return (
      <Suspense fallback={<PageLoader />}>
        <LazyCategoryPage 
          categoryId={categoryId}
          onBack={() => setActiveView('home')} 
          onNavigateTo={(target) => setActiveView(target)}
          onOpenAiChat={() => setShowAiChat(true)}
          selectedLocation={selectedLocation}
          user={user}
        />
      </Suspense>
    );
  }

  if (activeView === 'pb-gesprekken') {
    return <Suspense fallback={<PageLoader />}><LazyPBGesprekkenPage onBack={() => setActiveView('home')} user={user} isAdmin={isAdmin} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'weekmenu') {
    return <Suspense fallback={<PageLoader />}><LazyWeekmenuPage onBack={() => setActiveView('home')} user={user} isAdmin={isAdmin} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'boodschappen') {
    return <Suspense fallback={<PageLoader />}><LazyBoodschappenPage onBack={() => setActiveView('home')} user={user} isAdmin={isAdmin} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'corvee') {
    return <Suspense fallback={<PageLoader />}><LazyCorveePage onBack={() => setActiveView('home')} user={user} isAdmin={isAdmin} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'huisafspraken') {
    return <Suspense fallback={<PageLoader />}><LazyHuisafsprakenPage onBack={() => setActiveView('home')} user={user} /></Suspense>;
  }
  if (activeView === 'teamuitjes') {
    return <Suspense fallback={<PageLoader />}><LazyTeamuitjesPage onBack={() => setActiveView('home')} user={user} isAdmin={isAdmin} /></Suspense>;
  }
  if (activeView === 'vergaderingen') {
    return <Suspense fallback={<PageLoader />}><LazyVergaderingenPage onBack={() => setActiveView('home')} user={user} isAdmin={isAdmin} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'fiches') {
    return <Suspense fallback={<PageLoader />}><LazyFichesPage onBack={() => setActiveView('home')} user={user} isAdmin={isAdmin} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'taken') {
    return <Suspense fallback={<PageLoader />}><LazyTakenPage onBack={() => setActiveView('home')} user={user} /></Suspense>;
  }
  if (activeView === 'clienten') {
    return <Suspense fallback={<PageLoader />}><LazyClientenPage onBack={() => setActiveView('home')} user={user} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'teamoverleg') {
    return <Suspense fallback={<PageLoader />}><LazyTeamoverlegPage onBack={() => setActiveView('home')} user={user} isAdmin={isAdmin} /></Suspense>;
  }
  if (activeView === 'mdo') {
    return <Suspense fallback={<PageLoader />}><LazyMDOPage onBack={() => setActiveView('home')} user={user} isAdmin={isAdmin} /></Suspense>;
  }
  if (activeView === 'medicatie') {
    return <Suspense fallback={<PageLoader />}><LazyMedicatiePage onBack={() => setActiveView('home')} user={user} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'checklist') {
    return <Suspense fallback={<PageLoader />}><LazyChecklistPage onBack={() => setActiveView('home')} user={user} /></Suspense>;
  }
  if (activeView === 'overdracht') {
    return <Suspense fallback={<PageLoader />}><LazyOverdrachtPage onBack={() => setActiveView('home')} user={user} /></Suspense>;
  }
  if (activeView === 'wifi') {
    return <Suspense fallback={<PageLoader />}><LazyWifiPage onBack={() => setActiveView('home')} user={user} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'afvalkalender') {
    return <Suspense fallback={<PageLoader />}><LazyAfvalkalenderPage onBack={() => setActiveView('home')} user={user} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'zorgplan-evaluaties') {
    return <Suspense fallback={<PageLoader />}><LazyZorgplanEvaluatiesPage onBack={() => setActiveView('home')} user={user} /></Suspense>;
  }
  if (activeView === 'rie-evaluaties') {
    return <Suspense fallback={<PageLoader />}><LazyRIEEvaluatiesPage onBack={() => setActiveView('home')} user={user} /></Suspense>;
  }
  if (activeView === 'revalidatie') {
    return <Suspense fallback={<PageLoader />}><LazyRevalidatiePage onBack={() => setActiveView('home')} user={user} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'evaluatie-overzicht') {
    return <Suspense fallback={<PageLoader />}><LazyEvaluatieOverzichtPage onBack={() => setActiveView('home')} user={user} /></Suspense>;
  }
  if (activeView === 'rooster') {
    return <Suspense fallback={<PageLoader />}><LazyRoosterPage onBack={() => setActiveView('home')} user={user} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'afspraken') {
    return <Suspense fallback={<PageLoader />}><LazyAfsprakenPage onBack={() => setActiveView('home')} user={user} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'klusjes') {
    return <Suspense fallback={<PageLoader />}><LazyKlusjesPage onBack={() => setActiveView('home')} user={user} /></Suspense>;
  }
  if (activeView === 'dagbesteding') {
    return <Suspense fallback={<PageLoader />}><LazyDagbestedingPage onBack={() => setActiveView('home')} user={user} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'eetmomenten') {
    return <Suspense fallback={<PageLoader />}><LazyEetmomentenPage onBack={() => setActiveView('home')} user={user} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'voice-rapportage') {
    return <Suspense fallback={<PageLoader />}><LazyVoiceRapportagePage onBack={() => setActiveView('home')} user={user} selectedLocation={selectedLocation} /></Suspense>;
  }
  if (activeView === 'rapporteer-helper') {
    return <Suspense fallback={<PageLoader />}><LazyRapporteerHelperPage onBack={() => setActiveView('home')} userLocation={selectedLocation} userLocations={user?.locations} userRole={user?.role} /></Suspense>;
  }
  if (activeView === 'rapportage-controle') {
    return <Suspense fallback={<PageLoader />}><LazyRapportageControlePage onBack={() => setActiveView('home')} user={user} /></Suspense>;
  }
  if (activeView === 'parkeer-hulp') {
    return <Suspense fallback={<PageLoader />}><LazyParkeerHulpPage onBack={() => setActiveView('home')} /></Suspense>;
  }
  if (activeView === 'hkz') {
    return <Suspense fallback={<PageLoader />}><LazyHKZPage onBack={() => setActiveView('home')} user={user} /></Suspense>;
  }
  if (activeView === 'inwerkbot') {
    return <Suspense fallback={<PageLoader />}><LazyInwerkbotPage onBack={() => setActiveView('home')} user={user} /></Suspense>;
  }
  if (activeView === 'pb-overzicht') {
    return <Suspense fallback={<PageLoader />}><LazyPBOverzichtPage onBack={() => setActiveView('home')} locationSlug={selectedLocation} /></Suspense>;
  }

  // Als geen specifieke view, toon HomeScreen (geen groot dashboard meer)
  return (
    <HomeScreen
      user={user}
      selectedLocation={selectedLocation}
      onLocationChange={(newLocation) => setSelectedLocation(newLocation)}
      notifications={{}}
      onNavigateTo={(target) => setActiveView(target)}
      onOpenAiChat={() => setShowAiChat(true)}
      onLogout={logout}
    />
  );
}
